/**
 * ServerForm — Odoo-style full-screen detail view
 * Tabs: Identificación / Ubicación / Hardware / Licencia / Organización
 * SmartButtons: status, roles, rack, documents
 * Right sidebar: DocumentPanel or chatter
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
  TrashIcon,
  SparklesIcon,
  MapPinIcon,
  CubeIcon,
  TagIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  CircleStackIcon,
  DocumentDuplicateIcon,
  UserIcon,
  BuildingOfficeIcon,
  PencilSquareIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import ServerDisksPanel from './ServerDisksPanel';
import ServerComponentsPanel from './ServerComponentsPanel';
import SoftwareInventoryPanel from './SoftwareInventoryPanel';
import StorageVolumesPanel from './StorageVolumesPanel';
import StorageLunsPanel from './StorageLunsPanel';
import SystemUsersPanel from './SystemUsersPanel';
import ElementIpPanel from './ElementIpPanel';
import LocationTreeSelect from './common/LocationTreeSelect';
import DeviceAccessPanel from './DeviceAccessPanel';
import NetworkConnectionsPanel from './NetworkConnectionsPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import DocSmartButton from './DocSmartButton';
import ContactsSmartButton from './ContactsSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ServerChangelog from './ServerChangelog';
import ServerMonitoringPanel from './ServerMonitoringPanel';
import { useSocket } from '../contexts/SocketContext';
import { getServerSetupWarnings, normalizeSetupOmitted } from '../utils/serverSetupWarnings';
import Dialog from './common/Dialog';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import useAgentUpdateStatus, { describeAgentUpdate } from '../hooks/useAgentUpdateStatus';
import '../styles/DetailView.css';
import DetailCrumb from './DetailCrumb';
import ServerFormCardsHeader from './ServerFormCardsHeader';
import ServerFormSummary from './ServerFormSummary';
import ServerCardsHardware from './ServerCardsHardware';
import ConnectionDiagram from './ConnectionDiagram';
import RemoteActions from './RemoteActions';
import ServerCardsNetwork from './ServerCardsNetwork';
import ServerCardsStorage from './ServerCardsStorage';
import ServerCardsIps from './ServerCardsIps';
import ServerCardsIpManager from './ServerCardsIpManager';
import './ServerFormCards.css';


function maskAgentIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (raw.length <= 8) return '***';
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
}

function logServerAgentUiEvent(event, details = {}) {
  console.info(`[AGENT_UI:server_form] ${event}`, {
    ts: new Date().toISOString(),
    ...details,
  });
}

function getStorageAdvisory(record) {
  const warnings = record?.data?.agent_warnings;
  if (Array.isArray(warnings) && warnings.length > 0) {
    return warnings.find((warning) => warning?.code === 'windows_vroc_storage_review_recommended')
      || warnings.find((warning) => warning?.code === 'windows_vroc_crystaldiskinfo_required')
      || warnings[0];
  }
  const requirements = record?.data?.agent_requirements || {};
  if (requirements.storage_review?.required) {
    return {
      code: 'windows_vroc_storage_review_recommended',
      severity: 'warning',
      title: 'Storage detectado parcialmente: revisar inventario',
      message: requirements.storage_review.reason,
      instructions: requirements.storage_review.instructions || [],
    };
  }
  return null;
}

function getStorageAdvisoryEyebrow(advisory) {
  switch (advisory?.code) {
    case 'windows_vroc_crystaldiskinfo_required':
      return 'Dependencia operativa';
    case 'windows_vroc_storage_review_recommended':
      return 'Revision manual recomendada';
    default:
      return 'Aviso de storage';
  }
}

function detectHypervisorFromStorageBlob(storageBlob) {
  const roles = Array.isArray(storageBlob?.windows_roles) ? storageBlob.windows_roles : [];
  const software = Array.isArray(storageBlob?.installed_software) ? storageBlob.installed_software : [];

  const roleText = roles
    .map((r) => String(r?.display_name || r?.name || '').toLowerCase())
    .filter(Boolean);
  const softwareText = software
    .map((s) => String(s?.name || '').toLowerCase())
    .filter(Boolean);

  const hasHyperVRole = roleText.some((name) => name.includes('hyper-v'));
  const hasVmwareStack = softwareText.some((name) =>
    name.includes('vmware') || name.includes('esxi') || name.includes('vcenter')
  );

  if (hasHyperVRole) {
    return {
      isDetected: true,
      type: 'hyperv',
      label: 'Hyper-V',
      evidence: 'roles',
    };
  }
  if (hasVmwareStack) {
    return {
      isDetected: true,
      type: 'vmware',
      label: 'VMware',
      evidence: 'software',
    };
  }
  return {
    isDetected: false,
    type: null,
    label: null,
    evidence: null,
  };
}

function detectHypervisorState(server, storageBlob) {
  const persistedTypeRaw = String(server?.detected_hypervisor_type || '').trim().toLowerCase();
  const persistedType = persistedTypeRaw === 'hyper-v' ? 'hyperv' : persistedTypeRaw;
  if (server?.is_hypervisor) {
    return {
      isDetected: true,
      type: persistedType || null,
      label: persistedType === 'hyperv' ? 'Hyper-V' : persistedType === 'vmware' ? 'VMware' : 'Hipervisor',
      evidence: server?.hypervisor_detected_via || 'server_configuration',
      source: 'server',
    };
  }

  const fallback = detectHypervisorFromStorageBlob(storageBlob);
  return {
    ...fallback,
    source: fallback.isDetected ? 'storage_blob' : 'none',
  };
}

// Intervalos ofrecidos en la ficha. El backend acepta de 5 min a 7 días; el
// ajuste guardado puede ser otro (se añade a la lista para no perderlo).
const AUTO_SYNC_INTERVAL_OPTIONS = [15, 30, 60, 120, 360, 720, 1440];

function formatIntervalLabel(minutes) {
  const value = Number(minutes) || 0;
  if (value <= 0) return '';
  if (value % 1440 === 0) {
    const days = value / 1440;
    return days === 1 ? '1 día' : `${days} días`;
  }
  if (value % 60 === 0) return `${value / 60} h`;
  return `${value} min`;
}

function formatMergeValue(value) {
  if (value === null || value === undefined) return 'Vacio';
  if (typeof value === 'string') return value.trim() || 'Vacio';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Vacio';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildDefaultMergeResolutions(fieldPreview) {
  return (fieldPreview || []).reduce((acc, item) => {
    if (item?.resolution === 'conflict' && item?.field) {
      acc[item.field] = 'target';
    }
    return acc;
  }, {});
}

function formatMergeIpList(values) {
  if (!Array.isArray(values) || values.length === 0) return 'Ninguna';
  return values.join(', ');
}

function resolveValidatedInventoryUser(usersPayload) {
  const validated = usersPayload?.validated_user;
  if (!validated || typeof validated !== 'object') return '';
  const raw = String(validated.raw || '').trim();
  if (raw) return raw;
  const username = String(validated.username || '').trim();
  const domain = String(validated.domain || '').trim();
  if (username && domain) return `${domain}\\${username}`;
  if (username) return username;
  return '';
}

function buildMergeCandidateScore(source, candidate) {
  let score = 0;
  const sourceConfig = source?.configuration || {};
  const candidateConfig = candidate?.configuration || {};

  if (source?.serial_number && candidate?.serial_number && source.serial_number === candidate.serial_number) score += 50;
  if (source?.asset_tag && candidate?.asset_tag && source.asset_tag === candidate.asset_tag) score += 30;
  if (source?.agent_id && candidate?.agent_id && source.agent_id === candidate.agent_id) score += 25;
  if (source?.hostname && candidate?.hostname && source.hostname === candidate.hostname) score += 20;
  if (source?.ip_address && candidate?.ip_address && source.ip_address === candidate.ip_address) score += 15;
  if (source?.manufacturer && candidate?.manufacturer && source.manufacturer === candidate.manufacturer) score += 5;
  if (source?.model && candidate?.model && source.model === candidate.model) score += 5;
  if (sourceConfig.uuid && candidateConfig.uuid && sourceConfig.uuid === candidateConfig.uuid) score += 50;

  return score;
}

function extractDetectedRoleLabelsFromStorage(storageBlob) {
  if (!Array.isArray(storageBlob?.windows_roles)) return [];
  return storageBlob.windows_roles
    .filter((item) => String(item?.feature_type || '').trim().toLowerCase() === 'role')
    .map((item) => String(item?.display_name || item?.name || '').trim())
    .filter(Boolean);
}

function mergeServerRoleEntries(manualRoles = [], detectedRoleLabels = []) {
  const merged = [];
  const seen = new Set();

  manualRoles.forEach((role) => {
    const label = String(role?.code || role?.name || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({
      key: `manual-${role?.id || key}`,
      label,
      source: 'manual',
      roleId: role?.id || null,
    });
  });

  detectedRoleLabels.forEach((value) => {
    const label = String(value || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({
      key: `agent-${key}`,
      label,
      source: 'agent',
      roleId: null,
    });
  });

  return merged;
}

function ServerForm({ server, companyId, initialMode = 'create', onClose, onSave, onClone, cloningId, navItems, navIndex, onNavigate, onOpenServer, variant = 'classic' }) {
  const isExisting = Boolean(server?.id);
  const isCards = variant === 'cards';
  const navigate = useNavigate();

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isExisting || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ── Data ──
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  // ── Form ──
  const [form, setForm] = useState({
    name: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    asset_tag: '',
    hostname: '',
    ip_address: '',
    os: '',
    environment: 'prod',
    status: 'active',
    location_id: '',
    location_name: '',
    rack_id: '',
    rack_name: '',
    position_u: '',
    form_factor: 'rack',
    size_u: 1,
    role_ids: [],
    manual_roles: [],
    owner: '',
    license: '',
    license_status: 'vigente',
    license_expires_at: '',
    cpu: '',
    ram_gb: '',
    storage_tb: '',
    hypervisor: '',
    cluster: '',
    tags: '',
    chassis_id: '',
    chassis_node: '',
    // Active Directory / Domain Controller flags
    ad_dc: false,
    ad_domain: '',
    ad_site: '',
    ad_global_catalog: false,
    ad_fsmo_roles: [],
  });
  const [isDirty, setIsDirty] = useState(false);
  // Último nombre guardado: el título en línea se compara con esto y no con la
  // fila del listado (que puede venir desfasada tras renombrar).
  const [committedName, setCommittedName] = useState(server?.name || '');
  const nameDirty = (form.name || '') !== committedName;
  const [savedOk, setSavedOk] = useState(false);
  const [ipamNotice, setIpamNotice] = useState('');
  const [volumeCount, setVolumeCount] = useState(0);
  const [companyLuns, setCompanyLuns] = useState([]);
  const [lunCount, setLunCount] = useState(0);        // LUNs que publica este equipo
  const [serverPorts, setServerPorts] = useState([]); // puertos físicos para los caminos del LUN
  const [setupOmitted, setSetupOmitted] = useState([]);
  // Avisos ocultos SOLO durante esta visita (no se guardan en el servidor).
  const [setupHiddenNow, setSetupHiddenNow] = useState([]);

  // ── UI ──
  // En la variante tarjetas un servidor existente abre en «Resumen»; el alta
  // y la ficha clásica siguen abriendo en Identificación.
  const [activeTab, setActiveTab] = useState(
    variant === 'cards' ? 'resumen' : 'general'
  );
  const [showTabsMenu, setShowTabsMenu] = useState(false);
  const tabsMenuRef = React.useRef(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [apiError, setApiError] = useState('');
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [newModel, setNewModel] = useState('');

  // Cambios sin guardar al pasar de ficha o cerrar: Guardar y continuar /
  // Descartar / Cancelar. `handleSubmit` se define más abajo; la función se
  // evalúa al pulsar el botón, no al montar.
  const navGuard = useNavGuard({
    dirty: isDirty || nameDirty,
    saving,
    entity: 'este servidor',
    actionLabel: 'pasas a otro servidor o cierras la ficha',
    save: () => handleSubmit(null, { force: true, keepEditing: editing }),
  });

  // ── Discos ──
  const [showDisksPanel, setShowDisksPanel] = useState(false);
  const [diskCount, setDiskCount] = useState(0);
  const [storageAdvisory, setStorageAdvisory] = useState(null);
  // El aviso de capacidad se puede ocultar solo mientras la ficha está abierta:
  // no se persiste, así que al reabrir el servidor vuelve a salir si sigue el problema.
  const [storageAdvisoryDismissed, setStorageAdvisoryDismissed] = useState(false);
  const [storageBlob, setStorageBlob] = useState(null); // full storage blob for Organización tab
  const [showComponentsPanel, setShowComponentsPanel] = useState(false);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const [showIpManager, setShowIpManager] = useState(false);
  const [ipRefresh, setIpRefresh] = useState(0);
  const [componentsCount, setComponentsCount] = useState(null);
  const [nicsCount, setNicsCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [hasBmcComponent, setHasBmcComponent] = useState(false);
  const [bmcPortIds, setBmcPortIds] = useState([]);
  const [bmcIp, setBmcIp] = useState('');
  const [bmcIpLoading, setBmcIpLoading] = useState(false);
  const [promotingBmc, setPromotingBmc] = useState(false);
  const [ipCount, setIpCount] = useState(0);
  const [accessCount, setAccessCount] = useState(0);
  const [syncingNow, setSyncingNow] = useState(false);
  const [syncQueued, setSyncQueued] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncStep, setSyncStep] = useState('idle');        // idle | enqueueing | pending | running | done | error
  const [syncMode, setSyncMode] = useState('rescan');      // rescan | hypervisor_sync
  const [syncCommandId, setSyncCommandId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncNotice, setSyncNotice] = useState(null);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const [showSyncActionsMenu, setShowSyncActionsMenu] = useState(false);
  const [removingManualRoleId, setRemovingManualRoleId] = useState(null);
  const syncPollRef = React.useRef(null);
  const syncTimedOutRef = React.useRef(false);
  const syncSoftTimeoutRef = React.useRef(null);
  const syncHardTimeoutRef = React.useRef(null);
  const syncElapsedRef = React.useRef(null);
  const lastFirstScanStatusRef = React.useRef(null);
  const syncActionsMenuRef = React.useRef(null);

  // Al cambiar de servidor sin cerrar la ficha, el aviso vuelve a mostrarse.
  React.useEffect(() => {
    setStorageAdvisoryDismissed(false);
  }, [server?.id]);

  // Cierra el menú «Más» de pestañas al pulsar fuera (solo variante tarjetas).
  React.useEffect(() => {
    if (!showTabsMenu) return undefined;
    const onDown = (event) => {
      if (tabsMenuRef.current && !tabsMenuRef.current.contains(event.target)) {
        setShowTabsMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showTabsMenu]);

  // ── Sync elapsed timer ──
  React.useEffect(() => {
    if (syncStep === 'pending' || syncStep === 'running') {
      setSyncElapsed(0);
      syncElapsedRef.current = setInterval(() => setSyncElapsed(s => s + 1), 1000);
    } else {
      if (syncElapsedRef.current) { clearInterval(syncElapsedRef.current); syncElapsedRef.current = null; }
    }
    return () => { if (syncElapsedRef.current) clearInterval(syncElapsedRef.current); };
  }, [syncStep]);

  React.useEffect(() => {
    if (!showSyncActionsMenu) return undefined;

    const handlePointerDown = (event) => {
      if (!syncActionsMenuRef.current?.contains(event.target)) {
        setShowSyncActionsMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowSyncActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showSyncActionsMenu]);

  // ── Agent status (real-time) ──
  const socket = useSocket();
  const [agentStatus, setAgentStatus] = useState(server?.agent_status || 'unknown');
  const [agentLastSeen, setAgentLastSeen] = useState(server?.last_seen_at || null);
  const [agentRuntime, setAgentRuntime] = useState(null);
  const [showAgentSetup, setShowAgentSetup] = useState(false);
  // Estado de la última actualización remota: «Actualizando…» aparece desde que
  // se encola y «Actualizado x.y.z» solo cuando el latido lo confirma.
  const {
    status: agentUpdateStatus,
    markEnqueued: markAgentUpdateEnqueued,
  } = useAgentUpdateStatus(companyId, {
    ciType: 'rack_element',
    ciId: server?.id,
    agentId: server?.agent_id,
    enabled: Boolean(server?.id && server?.agent_id),
  });
  const agentUpdate = describeAgentUpdate(agentUpdateStatus);
  const [agentApiKey, setAgentApiKey] = useState(null);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [agentApiKeys, setAgentApiKeys] = useState([]);
  const [showMergePanel, setShowMergePanel] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeResolutions, setMergeResolutions] = useState({});
  const [mergeRelationResolutions, setMergeRelationResolutions] = useState({});
  const [mergeCandidatesLoading, setMergeCandidatesLoading] = useState(false);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergeExecuting, setMergeExecuting] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [mergeResult, setMergeResult] = useState(null);

  const hypervisorDetection = useMemo(
    () => detectHypervisorState(server, storageBlob),
    [server, storageBlob]
  );

  // ── Sincronización automática de hipervisor + VMs ──
  // El ajuste lo guarda el backend y lo aplica una tarea programada: aquí sólo
  // se pinta lo que él resuelve (valores efectivos incluidos).
  const [autoSync, setAutoSync] = useState(server?.auto_hypervisor_sync || null);
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [autoSyncError, setAutoSyncError] = useState(null);

  useEffect(() => {
    setAutoSync(server?.auto_hypervisor_sync || null);
    setAutoSyncError(null);
  }, [server?.id, server?.auto_hypervisor_sync]);

  const autoSyncEnabled = Boolean(autoSync?.effective_enabled);
  const autoSyncMinutes = autoSync?.effective_minutes ?? 60;

  const autoSyncIntervalOptions = useMemo(() => {
    const list = [...AUTO_SYNC_INTERVAL_OPTIONS];
    const override = autoSync?.minutes;
    if (override && !list.includes(override)) list.push(override);
    return list.sort((a, b) => a - b);
  }, [autoSync?.minutes]);

  const autoSyncLastLabel = useMemo(() => {
    const raw = server?.hypervisor_last_sync_at;
    const when = raw ? new Date(raw) : null;
    if (!when || Number.isNaN(when.getTime())) {
      return 'Todavía no consta ninguna sincronización.';
    }
    const failed = server?.hypervisor_last_sync_status && server?.hypervisor_last_sync_status !== 'done';
    return `Última: ${when.toLocaleString()}${failed ? ' (con error)' : ''}`;
  }, [server?.hypervisor_last_sync_at, server?.hypervisor_last_sync_status]);

  const handleAutoSyncChange = async (changes) => {
    if (!server?.id || !companyId || autoSyncSaving) return;
    setAutoSyncSaving(true);
    setAutoSyncError(null);
    logServerAgentUiEvent('hypervisor_auto_sync_change', {
      serverId: server.id,
      companyId,
      ...changes,
    });
    try {
      const res = await api.updateServerHypervisorAutoSync(server.id, changes, companyId);
      const fresh = res?.data?.auto_hypervisor_sync;
      if (fresh) setAutoSync(fresh);
    } catch (err) {
      setAutoSyncError(err?.message || 'No se pudo guardar el ajuste');
    } finally {
      setAutoSyncSaving(false);
    }
  };

  useEffect(() => {
    setAgentStatus(server?.agent_status || 'unknown');
    setAgentLastSeen(server?.last_seen_at || null);
    setAgentRuntime(server?.agent_runtime || null);
  }, [server?.agent_status, server?.last_seen_at, server?.agent_runtime]);

  useEffect(() => {
    if (!socket || !server?.id) return;
    const onAgentStatus = ({ id, ci_type, agent_status, last_seen_at, agent_runtime }) => {
      if ((ci_type && ci_type !== 'rack_element') || Number(id) !== Number(server.id)) {
        return;
      }
        setAgentStatus(agent_status);
        setAgentLastSeen(last_seen_at);
        if (agent_runtime) setAgentRuntime(agent_runtime);
    };
    socket.on('agent:status', onAgentStatus);
    return () => socket.off('agent:status', onAgentStatus);
  }, [socket, server?.id]);

  const agentTransportInfo = (() => {
    const mode = String(agentRuntime?.mode || '').trim().toLowerCase();
    const lastError = String(agentRuntime?.last_error || '').trim();
    if (mode === 'websocket') {
      return {
        mode,
        label: 'WebSocket realtime',
        detail: null,
      };
    }
    if (mode === 'polling') {
      return {
        mode,
        label: 'Polling fallback (WebSocket no disponible)',
        detail: lastError || null,
      };
    }
    return {
      mode: 'unknown',
      label: 'Canal desconocido (sin heartbeat de runtime)',
      detail: lastError || null,
    };
  })();

  useEffect(() => {
    if (!socket || !syncCommandId) return undefined;

    const onCommandStatus = (payload) => {
      const command = payload?.command;
      if (!command || Number(command.id) !== Number(syncCommandId)) return;

      const st = command.status;
      if (st === 'received' || st === 'pending') {
        setSyncStep('pending');
        return;
      }
      if (st === 'running') {
        setSyncStep('running');
        return;
      }
      if (st === 'done') {
        setSyncStep('done');
        setSyncResult(command.result || null);
        setSyncNotice(null);
        if (syncPollRef.current) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
        }
        if (syncSoftTimeoutRef.current) {
          clearTimeout(syncSoftTimeoutRef.current);
          syncSoftTimeoutRef.current = null;
        }
        if (syncHardTimeoutRef.current) {
          clearTimeout(syncHardTimeoutRef.current);
          syncHardTimeoutRef.current = null;
        }
        return;
      }
      if (st === 'error') {
        setSyncStep('error');
        setSyncError(command?.result?.stderr || command?.result?.error || 'El agente reportó un error');
        setSyncNotice(null);
        if (syncPollRef.current) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
        }
        if (syncSoftTimeoutRef.current) {
          clearTimeout(syncSoftTimeoutRef.current);
          syncSoftTimeoutRef.current = null;
        }
        if (syncHardTimeoutRef.current) {
          clearTimeout(syncHardTimeoutRef.current);
          syncHardTimeoutRef.current = null;
        }
      }
    };

    socket.on('agent:command_status', onCommandStatus);
    return () => socket.off('agent:command_status', onCommandStatus);
  }, [socket, syncCommandId]);

  // ── Load API keys when agent setup panel opens ──
  const loadAgentApiKeys = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await api.getAgentApiKeys(companyId);
      setAgentApiKeys(res.api_keys || []);
    } catch { /* ignore */ }
  }, [companyId]);

  useEffect(() => {
    if (showAgentSetup) loadAgentApiKeys();
  }, [showAgentSetup, loadAgentApiKeys]);

  useEffect(() => {
    setShowMergePanel(false);
    setMergeCandidates([]);
    setMergeSearch('');
    setMergeTargetId('');
    setMergePreview(null);
    setMergeResolutions({});
    setMergeRelationResolutions({});
    setMergeError('');
    setMergeResult(null);
  }, [server?.id]);

  // ── Chassis ──
  const [chassisList, setChassisList] = useState([]);

  // ── Load disk count ──
  useEffect(() => {
    if (!server?.id || !companyId) {
      setStorageAdvisory(null);
      return;
    }
    api.getServerStorage(server.id, companyId)
      .then(r => {
        const record = r.data;
        const disks = record?.data?.physical_disks;
        setDiskCount(Array.isArray(disks) ? disks.length : 0);
        setStorageAdvisory(getStorageAdvisory(record));
        setStorageBlob(record?.data || null);
      })
      .catch(() => {
        setDiskCount(0);
        setStorageAdvisory(null);
        setStorageBlob(null);
      });
  }, [server?.id, companyId]);

  const refreshComponentsCount = async () => {
    if (!server?.id || !companyId) return;
    try {
      const res = await api.getComponents(server.id, companyId);
      const comps = Array.isArray(res?.data) ? res.data : [];
      setComponentsCount(comps.length);
      setNicsCount(comps.filter(c => c.component_type === 'nic').length);
      const bmcComp = comps.find(c => c.component_type === 'bmc');
      setHasBmcComponent(Boolean(bmcComp));
      setBmcPortIds((bmcComp?.ports || []).map(p => p.id).filter(Boolean));
      if (!bmcComp) setBmcIp('');
      // Puertos físicos (nics, hbas…): cada camino de un LUN puede colgar de uno
      // de ellos, que es el que va cableado.
      const ports = [];
      comps.forEach(comp => {
        (comp.ports || []).forEach(port => {
          ports.push({
            id: port.id,
            port_number: port.port_number,
            label: port.label,
            port_type: port.port_type,
            mac_address: port.mac_address,
            wwn: port.wwn,
            _component: comp.slot || comp.component_type,
          });
        });
      });
      setServerPorts(ports);
    } catch {
      setComponentsCount(0);
      setNicsCount(0);
      setHasBmcComponent(false);
      setBmcPortIds([]);
      setBmcIp('');
      setServerPorts([]);
    }
  };

  useEffect(() => {
    // Al volver a General se recargan los componentes: si la interfaz BMC/iLO se
    // creó desde Red / IP, aquí ya se ve en lectura y con la IP del IPAM.
    if (activeTab !== 'resumen' && activeTab !== 'general') return;
    refreshComponentsCount();
  }, [server?.id, companyId, ipCount, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── IP de la interfaz BMC / iLO ────────────────────────────────────────────
  // Cuando el servidor tiene interfaz BMC cableable, la IP deja de escribirse en
  // Identificación: la fuente de verdad es la asignación de Red / IP (IPAM) de
  // esa interfaz. Aquí se lee para mostrarla en modo lectura.
  const loadBmcIp = useCallback(async () => {
    if (!server?.id || !companyId || !hasBmcComponent) {
      setBmcIp('');
      return;
    }
    setBmcIpLoading(true);
    try {
      const [serverRes, elementRes] = await Promise.all([
        api.getIpamAssignments(companyId, {
          object_type: 'server',
          object_id: server.id,
        }).catch(() => ({ data: [] })),
        api.getIpamAssignments(companyId, {
          object_type: 'rack_element',
          object_id: server.id,
        }).catch(() => ({ data: [] })),
      ]);
      const refs = new Set(
        (bmcPortIds || []).flatMap(id => [`nic-${id}`, `port-${id}`])
      );
      refs.add('bmc');
      const active = [...(serverRes?.data || []), ...(elementRes?.data || [])]
        .filter(a => a && a.status === 'active');
      const assignment = active.find(a => a.interface_ref && refs.has(a.interface_ref));
      setBmcIp(assignment?.ip || '');
    } finally {
      setBmcIpLoading(false);
    }
  }, [server?.id, companyId, hasBmcComponent, bmcPortIds]);

  useEffect(() => {
    if (hasBmcComponent) loadBmcIp();
  }, [activeTab, hasBmcComponent, loadBmcIp, ipCount]);

  // Mantiene el campo espejo `ip_address` alineado con IPAM (la IP de la iLO).
  useEffect(() => {
    if (!hasBmcComponent || !bmcIp) return;
    setForm(prev => (prev.ip_address === bmcIp ? prev : { ...prev, ip_address: bmcIp }));
  }, [hasBmcComponent, bmcIp]);

  const refreshUsersCount = useCallback(async () => {
    if (!server?.id || !companyId) {
      setUsersCount(0);
      return;
    }
    try {
      const res = await api.getServerUsers(server.id, companyId);
      const data = res?.data?.data || res?.data || {};
      const preferredUser = resolveValidatedInventoryUser(data);
      const sessions = Array.isArray(data.current_sessions) ? data.current_sessions.length : 0;
      const localUsers = Array.isArray(data.local_users) ? data.local_users.length : 0;
      const validated = data.validated_user ? 1 : 0;
      setUsersCount(Math.max(sessions, localUsers, validated));
      if (preferredUser) {
        setForm((prev) => (prev.owner ? prev : { ...prev, owner: preferredUser }));
      }
    } catch {
      setUsersCount(0);
    }
  }, [server?.id, companyId]);

  useEffect(() => {
    refreshUsersCount();
  }, [refreshUsersCount]);

  // ── Load supporting data ──
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    api.getRacks(null, companyId).then(r => setRacks(r.data || [])).catch(() => {});
    api.getChassis({ company_id: companyId }).then(r => setChassisList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.getRoles(companyId, { objectType: 'server', includeGlobal: true })
      .then(r => setRoleOptions(r.data || []))
      .catch(() => setRoleOptions([]));
  }, [companyId]);

  const loadCatalogs = useCallback(async () => {
    if (!companyId) {
      setManufacturerOptions([]);
      setModelOptions([]);
      return;
    }
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'server' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'server' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) { /* ignore catalog load errors */ }
  }, [companyId]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  // ── Populate form from prop ──
  useEffect(() => {
    if (!server) return;
    setSyncQueued(false);
    setSetupOmitted(normalizeSetupOmitted(server.setup_omitted));
    // Los avisos silenciados duran solo esta visita: al cambiar de servidor se
    // vuelven a mostrar los que no estén omitidos de forma permanente.
    setSetupHiddenNow([]);
    setForm(prev => ({
      ...prev,
      ...server,
      location_id: server.location_id || prev.location_id || '',
      rack_id: server.rack_id || '',
      rack_name: server.rack_name || '',
      license_status: server.license_status || 'vigente',
      environment: server.environment || 'prod',
      status: server.status || 'active',
      form_factor: server.form_factor || 'rack',
      position_u: server.position_u || server.position || '',
      size_u: server.size_u || 1,
      tags: Array.isArray(server.tags) ? server.tags.join(', ') : (server.tags || ''),
      role_ids: Array.isArray(server.roles)
        ? server.roles.map(r => r.id).filter(Boolean)
        : (server.role_ids || []),
      manual_roles: Array.isArray(server.manual_roles)
        ? server.manual_roles
        : (Array.isArray(server.roles) ? server.roles : []),
      chassis_id:   server.chassis_id   || '',
      chassis_node: server.chassis_node || '',
    }));
    setCommittedName(server.name || '');
  }, [server]);

  // Refresh with latest server payload to avoid stale list data in detail view
  useEffect(() => {
    if (!isExisting || !server?.id || !companyId) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await api.getServer(server.id, companyId);
        const fresh = res?.data || res?.server || null;
        if (!cancelled && fresh) {
          setForm(prev => ({
            ...prev,
            ...fresh,
            location_id: fresh.location_id || prev.location_id || '',
            rack_id: fresh.rack_id || '',
            rack_name: fresh.rack_name || '',
            license_status: fresh.license_status || 'vigente',
            environment: fresh.environment || 'prod',
            status: fresh.status || 'active',
            form_factor: fresh.form_factor || 'rack',
            position_u: fresh.position_u || fresh.position || '',
            size_u: fresh.size_u || 1,
            tags: Array.isArray(fresh.tags) ? fresh.tags.join(', ') : (fresh.tags || ''),
            role_ids: Array.isArray(fresh.roles)
              ? fresh.roles.map(r => r.id).filter(Boolean)
              : (fresh.role_ids || []),
            manual_roles: Array.isArray(fresh.manual_roles)
              ? fresh.manual_roles
              : (Array.isArray(fresh.roles) ? fresh.roles : []),
            chassis_id: fresh.chassis_id || '',
            chassis_node: fresh.chassis_node || '',
            domain_name: fresh.domain_name ?? null,
            part_of_domain: fresh.part_of_domain ?? null,
          }));
          setAgentStatus(fresh.agent_status || 'unknown');
          setAgentLastSeen(fresh.last_seen_at || null);
          if (fresh.agent_runtime) setAgentRuntime(fresh.agent_runtime);
        }
      } catch (_) {
        // keep optimistic prop data if refresh fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isExisting, server?.id, companyId]);

  // ── Derived ──
  const formFactor = form.form_factor || 'rack';
  const filteredRacks = useMemo(() => {
    if (!form.location_id) return racks;
    return racks.filter(r => String(r.location_id) === String(form.location_id));
  }, [racks, form.location_id]);
  const selectedRack = useMemo(() =>
    filteredRacks.find(r => String(r.id) === String(form.rack_id)) ||
    racks.find(r => String(r.id) === String(form.rack_id)),
    [filteredRacks, racks, form.rack_id]
  );
  const rackHeight = selectedRack?.height_u ? Number(selectedRack.height_u) : null;
  const sizeVal = Number(form.size_u || 0);
  const positionVal = form.position_u ? Number(form.position_u) : null;
  const bottomU = positionVal && sizeVal ? positionVal - sizeVal + 1 : null;
  const rangeLabel = positionVal && sizeVal
    ? (sizeVal === 1 ? `U${positionVal}` : `U${positionVal} – U${bottomU}`)
    : null;
  const isNode  = formFactor === 'node';
  const showSizeU = !isNode && (formFactor === 'rack' || (formFactor === 'tower' && Boolean(form.rack_id)));
  const selectedRoles = useMemo(
    () => {
      const ids = Array.isArray(form.role_ids) ? form.role_ids.map(Number).filter(Boolean) : [];
      // Prefer fresh metadata from roleOptions when available; fall back to manual_roles cache
      const cache = Array.isArray(form.manual_roles)
        ? form.manual_roles
        : (Array.isArray(server?.manual_roles)
            ? server.manual_roles
            : (Array.isArray(server?.roles) ? server.roles : []));
      const cacheById = new Map(
        (cache || []).filter(r => r && r.id).map(r => [Number(r.id), r])
      );
      return ids
        .map(id => {
          const fromOptions = roleOptions.find(r => Number(r.id) === id);
          return fromOptions || cacheById.get(id) || null;
        })
        .filter(Boolean);
    },
    [form.role_ids, form.manual_roles, server?.manual_roles, server?.roles, roleOptions]
  );
  const detectedRoleLabels = useMemo(() => {
    const storageLabels = extractDetectedRoleLabelsFromStorage(storageBlob);
    if (storageLabels.length > 0) return storageLabels;
    return Array.isArray(server?.detected_roles) ? server.detected_roles : [];
  }, [storageBlob, server?.detected_roles]);
  const effectiveRoleEntries = useMemo(
    () => mergeServerRoleEntries(selectedRoles, detectedRoleLabels),
    [selectedRoles, detectedRoleLabels]
  );
  const effectiveRolesCount = effectiveRoleEntries.length;

  const setupWarnings = useMemo(() => (
    getServerSetupWarnings(
      {
        ...server,
        ...form,
        role_ids: form.role_ids || [],
        roles: selectedRoles,
        windows_roles: storageBlob?.windows_roles || [],
        setup_omitted: setupOmitted,
      },
      { hasBmcComponent }
    )
  ), [server, form, selectedRoles, storageBlob, setupOmitted, hasBmcComponent]);

  const omittedSetupItems = useMemo(() => {
    const labels = {
      location: 'Ubicación',
      roles: 'Roles',
      bmc: 'BMC / iLO',
    };
    return normalizeSetupOmitted(setupOmitted).map((key) => ({
      key,
      label: labels[key] || key,
    }));
  }, [setupOmitted]);

  // Un aviso omitido "para siempre" o silenciado en esta visita desaparece de
  // la pantalla; si desaparecen todos, el bloque entero se va y solo queda un
  // enlace discreto para volver a mostrarlos.
  const setupWarningsVisibles = useMemo(
    () => setupWarnings.filter((w) => !setupHiddenNow.includes(w.key)),
    [setupWarnings, setupHiddenNow]
  );
  const avisosOcultosCount = useMemo(() => {
    const claves = new Set([...normalizeSetupOmitted(setupOmitted), ...setupHiddenNow]);
    return claves.size;
  }, [setupOmitted, setupHiddenNow]);

  const statusColors = {
    active: { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
    maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
    decom: { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
  };
  const currentStatus = statusColors[form.status] || statusColors.active;

  const firstScanUi = useMemo(() => {
    const status = String(server?.first_scan_status || '').trim().toLowerCase();
    if (!status) return null;
    if (status === 'completed') {
      return {
        label: 'Primer scan: completado',
        style: { background: 'rgba(34,197,94,.14)', color: '#16a34a' },
      };
    }
    if (status === 'running') {
      return {
        label: 'Primer scan: en ejecucion',
        style: { background: 'rgba(59,130,246,.14)', color: '#2563eb' },
      };
    }
    if (status === 'pending') {
      return {
        label: 'Primer scan: pendiente',
        style: { background: 'rgba(245,158,11,.15)', color: '#d97706' },
      };
    }
    if (status === 'error') {
      const errorText = server?.first_scan_error ? ` (${server.first_scan_error})` : '';
      return {
        label: `Primer scan: error${errorText}`,
        style: { background: 'rgba(239,68,68,.14)', color: '#dc2626' },
      };
    }
    return null;
  }, [server]);

  useEffect(() => {
    const status = String(server?.first_scan_status || '').trim().toLowerCase();
    const error = server?.first_scan_error || null;
    const previousStatus = lastFirstScanStatusRef.current;
    if (!status && !previousStatus) return;
    if (status === previousStatus && !error) return;
    logServerAgentUiEvent('first_scan_status_observed', {
      serverId: server?.id,
      agentId: maskAgentIdentifier(server?.agent_id),
      previousStatus,
      status,
      error,
    });
    lastFirstScanStatusRef.current = status;
  }, [server]);

  const envColors = {
    prod: { label: '🟢 Producción' },
    pre: { label: '🟡 Pre-producción' },
    dev: { label: '🔵 Desarrollo' },
  };

  const filteredMergeCandidates = useMemo(() => {
    const normalizedSearch = mergeSearch.trim().toLowerCase();
    return (mergeCandidates || [])
      .filter((candidate) => {
        if (!candidate?.id || Number(candidate.id) === Number(server?.id)) return false;
        if ((candidate.status || '').toLowerCase() === 'merged') return false;
        if (!normalizedSearch) return true;
        return [
          candidate.name,
          candidate.hostname,
          candidate.ip_address,
          candidate.serial_number,
          candidate.asset_tag,
          candidate.location_name,
          candidate.rack_name,
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
      })
      .sort((left, right) => {
        const scoreDiff = buildMergeCandidateScore(server, right) - buildMergeCandidateScore(server, left);
        if (scoreDiff !== 0) return scoreDiff;
        return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base' });
      });
  }, [mergeCandidates, mergeSearch, server]);

  const mergeConflictFields = useMemo(() => (
    (mergePreview?.field_preview || []).filter((item) => item.resolution === 'conflict')
  ), [mergePreview]);

  const mergeMovableRelations = useMemo(() => (
    (mergePreview?.relation_preview || []).filter((item) => item.source_count > 0)
  ), [mergePreview]);

  const mergeIpAssignmentsRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'ip_assignments') || null
  ), [mergePreview]);

  const mergeVirtualIpRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'virtual_ip_memberships') || null
  ), [mergePreview]);

  const mergeComponentsRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'components') || null
  ), [mergePreview]);

  const mergePowerRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'power_connections') || null
  ), [mergePreview]);

  const mergeIncomingSwitchLinksRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'incoming_switch_links') || null
  ), [mergePreview]);

  const mergeIncomingPatchLinksRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((item) => item.key === 'incoming_patch_links') || null
  ), [mergePreview]);

  const mergeUnresolvedBlocks = useMemo(() => (
    (mergePreview?.blocks || []).filter((block) => {
      if (block.key === 'ip_assignments') {
        return !mergeRelationResolutions.ip_assignments;
      }
      if (block.key === 'virtual_ip_memberships') {
        return !mergeRelationResolutions.virtual_ip_memberships;
      }
      if (block.key === 'components') {
        return !mergeRelationResolutions.components;
      }
      if (block.key === 'power_connections') {
        return !mergeRelationResolutions.power_connections;
      }
      if (block.key === 'incoming_switch_links') {
        return !mergeRelationResolutions.incoming_switch_links;
      }
      if (block.key === 'incoming_patch_links') {
        return !mergeRelationResolutions.incoming_patch_links;
      }
      return true;
    })
  ), [mergePreview, mergeRelationResolutions]);

  const canExecuteMerge = Boolean(
    mergePreview && mergeTargetId && mergeUnresolvedBlocks.length === 0
  );

  const selectedMergeCandidate = useMemo(() => (
    mergeCandidates.find((candidate) => String(candidate.id) === String(mergeTargetId)) || null
  ), [mergeCandidates, mergeTargetId]);

  // ── Handlers ──
  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setApiError('');
    setErrors([]);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'form_factor') {
      if (value === 'node') {
        // Switching to node → clear rack/position, keep location
        setForm(prev => ({ ...prev, form_factor: value, rack_id: '', rack_name: '', position_u: '', size_u: 1 }));
      } else {
        // Switching away from node → clear chassis fields
        setForm(prev => ({ ...prev, form_factor: value, chassis_id: '', chassis_node: '' }));
      }
      setIsDirty(true); setApiError(''); setErrors([]);
      return;
    }
    setField(name, value);
  };

  const handleLocationChange = async e => {
    const value = e.target.value;
    const loc = locations.find(l => String(l.id) === value);
    setForm(prev => ({
      ...prev,
      location_id: value,
      location_name: loc?.name || '',
      rack_id: '',
      rack_name: '',
    }));
    setIsDirty(true);
    try {
      const r = await api.getRacks(value || null, companyId);
      setRacks(r.data || []);
    } catch (_) {}
  };

  const handleRackChange = e => {
    const value = e.target.value;
    const rack = racks.find(r => String(r.id) === value);
    setForm(prev => ({
      ...prev,
      rack_id: value,
      rack_name: rack?.name || rack?.code || '',
      location_id: rack?.location_id ? String(rack.location_id) : prev.location_id,
      location_name: rack?.location_name || prev.location_name,
    }));
    setIsDirty(true);
  };

  const handleRolesChange = e => {
    const ids = Array.from(e.target.selectedOptions || [])
      .map(opt => Number(opt.value)).filter(Boolean);
    setField('role_ids', ids);
  };

  const handleManufacturerChange = (value) => {
    const antes = form.manufacturer || '';
    setField('manufacturer', value);
    // El modelo pertenece al fabricante: al cambiarlo, el anterior ya no vale.
    if (antes !== (value || '') && form.model) setField('model', '');
  };

  const handleModelChange = (value) => {
    setField('model', value);
  };

  const saveManufacturerToCatalog = async (nameOverride) => {
    const name = nameOverride || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'server' }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar fabricante');
    }
  };

  const saveModelToCatalog = async (modelNameOverride) => {
    const modelName = modelNameOverride || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'server', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar modelo');
    }
  };

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const handleDiscard = () => {
    // En una ficha existente, descartar vuelve a modo LECTURA (así siempre hay
    // salida de la edición aunque no se haya tocado nada). En un alta, cierra.
    if (server) {
      setForm(prev => ({
        ...prev,
        ...server,
        tags: Array.isArray(server.tags) ? server.tags.join(', ') : (server.tags || ''),
        role_ids: Array.isArray(server.roles)
          ? server.roles.map(r => r.id).filter(Boolean)
          : (server.role_ids || []),
      }));
      setEditing(false);
    } else {
      onClose();
    }
    setIsDirty(false);
    setErrors([]);
    setApiError('');
  };

  /**
   * Pasar al servidor anterior/siguiente desde el paginador.
   *
   * Si hay cambios sin guardar se abre `navGuard` (Guardar y continuar /
   * Descartar / Cancelar). NO se sale del modo edición ni se cambia de
   * pestaña: la ficha del siguiente servidor abre en el mismo sitio y en
   * edición, que es lo que permite encadenar varios equipos seguidos.
   */
  const irANav = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setApiError('');
      onNavigate?.(destino);
    });
  };

  const handleSubmit = async (e, { force = false, keepEditing = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    // `force`: guardar el nombre desde el título en reposo (Enter).
    if (!editing && !force) return false;
    setSaving(true);
    setApiError('');
    try {
      const errs = [];
      if (!form.name) errs.push('El nombre es obligatorio');
      // El hostname NO es obligatorio: hay equipos inventariados sin FQDN y
      // bloqueaba guardar el resto de la ficha (marca, modelo, nº de serie…).
      // Se recomienda en «Completitud de la ficha», no se exige.
      if (form.form_factor === 'node') {
        if (!form.chassis_id) errs.push('Debe seleccionar un chasis para un nodo');
      } else {
        if (!form.location_id) errs.push('La ubicación es obligatoria');
      }
      if (showSizeU && Number(form.size_u) < 1) errs.push('Las U deben ser >= 1');
      if (errs.length) { setErrors(errs); setSaving(false); return false; }

      const loc = locations.find(l => String(l.id) === String(form.location_id));
      const rack = racks.find(r => String(r.id) === String(form.rack_id));

      // For node form_factor, inherit location from chassis
      let locationId = form.location_id ? Number(form.location_id) : null;
      let rackId     = form.rack_id     ? Number(form.rack_id)     : null;
      if (form.form_factor === 'node' && form.chassis_id) {
        const ch = chassisList.find(c => String(c.id) === String(form.chassis_id));
        if (ch) {
          locationId = ch.location_id || locationId;
          rackId     = ch.rack_id     || null;
        }
      }

      const { role_ids, roles, ...formBase } = form;
      const payload = {
        ...formBase,
        role_ids: Array.isArray(role_ids) ? role_ids : [],
        setup_omitted: normalizeSetupOmitted(setupOmitted),
        location_id: locationId,
        rack_id: rackId,
        position_u: form.form_factor === 'node' ? null : (form.position_u ? Number(form.position_u) : null),
        size_u: form.form_factor === 'node' ? 1 : (showSizeU ? Number(form.size_u || 1) : undefined),
        ram_gb: form.ram_gb ? Number(form.ram_gb) : undefined,
        storage_tb: form.storage_tb ? Number(form.storage_tb) : undefined,
        location_name: loc?.name || form.location_name,
        rack_name: rack?.name || rack?.code || form.rack_name,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        chassis_id:   form.chassis_id   ? Number(form.chassis_id)   : null,
        chassis_node: form.chassis_node ? Number(form.chassis_node) : null,
      };
      const resp = isExisting
        ? await api.updateServer(server.id, payload, companyId)
        : await api.createServer(payload, companyId);

      if (!resp?.success && resp?.message) throw new Error(resp.message);
      setIsDirty(false);
      setCommittedName(form.name || '');
      setSavedOk(true);
      // Si al cambiar la IP el backend liberó la anterior en el IPAM, se avisa:
      // antes esa IP se quedaba ocupada y no se podía reutilizar.
      const released = resp?.ipam_released_ips || [];
      if (released.length) {
        setIpamNotice(
          `IP liberada en el IPAM: ${released.join(', ')} (ya se puede reutilizar)`
        );
        setTimeout(() => setIpamNotice(''), 8000);
      } else {
        setIpamNotice('');
      }
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSave?.(isExisting ? null : registroGuardado(resp));
      if (isExisting && !keepEditing) {
        // Guardar desde el botón normal vuelve a LECTURA (estándar del CMDB).
        // Al pasar de ficha con «Guardar y continuar» se mantiene la edición.
        setEditing(false);
      }
      return true;
    } catch (err) {
      setApiError(err.message || 'Error guardando servidor');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveManualRole = async (role) => {
    const roleId = Number(role?.id || 0);
    if (!server?.id || !companyId || !roleId) return;
    setRemovingManualRoleId(roleId);
    setApiError('');
    try {
      const resp = await api.deleteServerManualRole(server.id, roleId, companyId);
      const updated = resp?.data || null;
      if (updated) {
        setForm((prev) => ({
          ...prev,
          manual_roles: Array.isArray(updated.manual_roles)
            ? updated.manual_roles
            : (Array.isArray(updated.roles) ? updated.roles : []),
          role_ids: Array.isArray(updated.manual_roles)
            ? updated.manual_roles.map((item) => item.id).filter(Boolean)
            : [],
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          manual_roles: (Array.isArray(prev.manual_roles) ? prev.manual_roles : []).filter(
            (item) => Number(item?.id) !== roleId
          ),
          role_ids: (Array.isArray(prev.role_ids) ? prev.role_ids : []).filter(
            (item) => Number(item) !== roleId
          ),
        }));
      }
      onSave?.();
    } catch (err) {
      setApiError(err.message || 'No se pudo eliminar el rol manual');
    } finally {
      setRemovingManualRoleId(null);
    }
  };

  const toggleSetupOmit = async (checkKey) => {
    if (!checkKey) return;
    const next = (() => {
      const current = new Set(normalizeSetupOmitted(setupOmitted));
      if (current.has(checkKey)) current.delete(checkKey);
      else current.add(checkKey);
      return Array.from(current);
    })();

    setSetupOmitted(next);
    setIsDirty(true);

    if (!isExisting || !server?.id) return;
    try {
      await api.updateServer(server.id, { setup_omitted: next }, companyId);
      onSave?.();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar la omisión del aviso');
      setSetupOmitted(normalizeSetupOmitted(server.setup_omitted));
    }
  };

  /** Volver a mostrar todos los avisos: quita los silenciados y los omitidos. */
  const restaurarAvisos = async () => {
    setSetupHiddenNow([]);
    if (!normalizeSetupOmitted(setupOmitted).length) return;
    setSetupOmitted([]);
    setIsDirty(true);
    if (!isExisting || !server?.id) return;
    try {
      await api.updateServer(server.id, { setup_omitted: [] }, companyId);
      onSave?.();
    } catch (e) {
      setApiError(e.message || 'No se pudieron restaurar los avisos');
      setSetupOmitted(normalizeSetupOmitted(server.setup_omitted));
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setShowDeleteConfirm(false);
    try {
      await api.deleteServer(server.id, companyId);
      onClose?.();
    } catch (err) {
      setApiError(err.message || 'Error eliminando servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToStorage = async () => {
    if (!isExisting || !server?.id) return;
    const isStorageElement = (server?.element_type || '') === 'storage';
    const target = isStorageElement ? 'server' : 'storage';
    const question = isStorageElement
      ? '¿Convertir esta cabina en servidor? Seguirá mostrándose con el inventario completo del agente.'
      : '¿Convertir este servidor en cabina de almacenamiento? Seguirá recibiendo inventario del agente y aparecerá también en el módulo de Almacenamiento.';
    if (!window.confirm(question)) return;
    setSaving(true);
    setApiError('');
    try {
      await api.changeElementType(server.id, target, companyId);
      onSave?.();
      onClose?.();
    } catch (err) {
      setApiError(err.message || 'No se pudo convertir el elemento');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: 'server', companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) setForm(prev => ({ ...prev, name: s, hostname: prev.hostname || s }));
      setIsDirty(true);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  const handleSyncNow = async (mode = 'rescan') => {
    if (!server?.id || !server?.agent_id || !companyId) return;
    logServerAgentUiEvent('sync_click', {
      mode,
      serverId: server.id,
      companyId,
      agentId: maskAgentIdentifier(server.agent_id),
      hypervisorType: hypervisorDetection?.type || null,
    });
    setSyncMode(mode);
    setShowSyncModal(true);
    setSyncStep('enqueueing');
    setSyncResult(null);
    setSyncError(null);
    setSyncNotice(null);
    setSyncCommandId(null);
    syncTimedOutRef.current = false;

    if (syncSoftTimeoutRef.current) {
      clearTimeout(syncSoftTimeoutRef.current);
      syncSoftTimeoutRef.current = null;
    }
    if (syncHardTimeoutRef.current) {
      clearTimeout(syncHardTimeoutRef.current);
      syncHardTimeoutRef.current = null;
    }

    try {
      const command = mode === 'hypervisor_sync' ? 'hypervisor_sync' : 'rescan';
      const timeoutProfile = mode === 'hypervisor_sync'
        ? { softMs: 180000, hardMs: 900000 }
        : { softMs: 180000, hardMs: 480000 };
      const requestedFrom = mode === 'hypervisor_sync' ? 'hypervisor_sync_button' : 'sync_now_button';
      const payload = {
        source: 'server_form',
        requested_from: requestedFrom,
      };
      if (mode === 'hypervisor_sync' && hypervisorDetection?.type) {
        payload.hypervisor_type = hypervisorDetection.type;
      }
      const res = await api.enqueueAgentCommand(companyId, {
        agent_id: server.agent_id,
        command,
        ci_type: 'rack_element',
        ci_id: server.id,
        payload,
      });
      const cmdId = res.command?.id;
      if (!cmdId) {
        logServerAgentUiEvent('enqueue_missing_command_id', {
          mode,
          serverId: server.id,
          companyId,
          response: res,
        });
        setSyncStep('error');
        setSyncError('No se recibió ID de comando');
        return;
      }
      logServerAgentUiEvent('enqueue_ok', {
        mode,
        serverId: server.id,
        companyId,
        command,
        commandId: cmdId,
        agentId: maskAgentIdentifier(server.agent_id),
      });
      setSyncCommandId(cmdId);
      setSyncStep('pending');
      const pollStartedAt = Date.now();

      // Poll command status every 2s
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      syncPollRef.current = setInterval(async () => {
        if (syncTimedOutRef.current) return;
        try {
          const poll = await api.getAgentCommandStatus(companyId, cmdId);
          if (syncTimedOutRef.current) return;
          if (!poll?.command) {
            throw new Error('Respuesta invalida del backend: falta command');
          }
          const st = poll.command?.status;
          console.debug('[AGENT_UI:server_form] poll', {
            ts: new Date().toISOString(),
            mode,
            serverId: server.id,
            commandId: cmdId,
            status: st,
            elapsedMs: Date.now() - pollStartedAt,
          });
          if (st === 'running') setSyncStep('running');
          if (st === 'received') setSyncStep('pending');
          if (st === 'done') {
            setSyncStep('done');
            setSyncResult(poll.command?.result);
            setSyncNotice(null);
            logServerAgentUiEvent('command_done', {
              mode,
              serverId: server.id,
              commandId: cmdId,
              elapsedMs: Date.now() - pollStartedAt,
            });
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            if (syncSoftTimeoutRef.current) {
              clearTimeout(syncSoftTimeoutRef.current);
              syncSoftTimeoutRef.current = null;
            }
            if (syncHardTimeoutRef.current) {
              clearTimeout(syncHardTimeoutRef.current);
              syncHardTimeoutRef.current = null;
            }
          }
          if (st === 'error') {
            setSyncStep('error');
            setSyncError(poll.command?.result?.stderr || 'El agente reportó un error');
            setSyncNotice(null);
            logServerAgentUiEvent('command_error', {
              mode,
              serverId: server.id,
              commandId: cmdId,
              elapsedMs: Date.now() - pollStartedAt,
              result: poll.command?.result || null,
            });
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            if (syncSoftTimeoutRef.current) {
              clearTimeout(syncSoftTimeoutRef.current);
              syncSoftTimeoutRef.current = null;
            }
            if (syncHardTimeoutRef.current) {
              clearTimeout(syncHardTimeoutRef.current);
              syncHardTimeoutRef.current = null;
            }
          }
        } catch (pollError) {
          console.warn('[AGENT_UI:server_form] poll_failed', {
            ts: new Date().toISOString(),
            mode,
            serverId: server.id,
            commandId: cmdId,
            elapsedMs: Date.now() - pollStartedAt,
            error: pollError?.message || String(pollError),
          });
        }
      }, 2000);

      // Soft timeout only warns and keeps polling.
      syncSoftTimeoutRef.current = setTimeout(() => {
        if (syncPollRef.current && (syncStep === 'pending' || syncStep === 'running')) {
          setSyncNotice(
            mode === 'hypervisor_sync'
              ? 'Aviso: la sincronización de hipervisor/VMs está tardando más de lo habitual. Seguimos esperando resultado del agente.'
              : 'Aviso: la sincronización está tardando más de lo habitual. Seguimos esperando resultado del agente.'
          );
          logServerAgentUiEvent('command_soft_timeout', {
            mode,
            serverId: server.id,
            commandId: cmdId,
            elapsedMs: Date.now() - pollStartedAt,
          });
        }
      }, timeoutProfile.softMs);

      // Hard timeout terminates the UI wait.
      syncHardTimeoutRef.current = setTimeout(() => {
        if (syncPollRef.current) {
          syncTimedOutRef.current = true;
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncStep(prev => {
            if (prev === 'pending') {
              setSyncNotice(null);
              setSyncError(
                mode === 'hypervisor_sync'
                  ? 'Timeout: el agente no recogió el comando en 15 min. Verifica conectividad del agente y la cola de comandos.'
                  : 'Timeout: el agente no recogió el comando en 8 min. Verifica conectividad del agente y la cola de comandos.'
              );
              logServerAgentUiEvent('command_timeout_pending', {
                mode,
                serverId: server.id,
                commandId: cmdId,
                elapsedMs: Date.now() - pollStartedAt,
              });
              return 'error';
            }
            if (prev === 'running') {
              setSyncNotice(null);
              setSyncResult({ message: 'El agente sigue procesando. Los datos se actualizarán automáticamente cuando finalice.' });
              logServerAgentUiEvent('command_timeout_running', {
                mode,
                serverId: server.id,
                commandId: cmdId,
                elapsedMs: Date.now() - pollStartedAt,
              });
              return 'done';
            }
            return prev;
          });
        }
      }, timeoutProfile.hardMs);
    } catch (e) {
      setSyncStep('error');
      setSyncError(e.message || 'No se pudo encolar la sincronización');
      setSyncNotice(null);
      logServerAgentUiEvent('enqueue_failed', {
        mode,
        serverId: server?.id,
        companyId,
        agentId: maskAgentIdentifier(server?.agent_id),
        error: e?.message || String(e),
      });
    }
  };

  const handleUpdateAgentNow = async () => {
    if (!server?.id || !server?.agent_id || !companyId) return;
    setShowSyncActionsMenu(false);
    setUpdatingAgent(true);
    setApiError('');
    logServerAgentUiEvent('update_agent_click', {
      serverId: server.id,
      companyId,
      agentId: maskAgentIdentifier(server.agent_id),
    });
    try {
      await api.sendAgentUpdateCommand(companyId, {
        ci_type: 'rack_element',
        ci_id: server.id,
      });
      // Pinta «Actualizando…» al instante; el backend confirmará la versión
      // cuando el agente reinicie y vuelva a latir.
      markAgentUpdateEnqueued();
      logServerAgentUiEvent('update_agent_enqueued', {
        serverId: server.id,
        companyId,
        agentId: maskAgentIdentifier(server.agent_id),
      });
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (e) {
      logServerAgentUiEvent('update_agent_failed', {
        serverId: server?.id,
        companyId,
        agentId: maskAgentIdentifier(server?.agent_id),
        error: e?.message || String(e),
      });
      setApiError(e.message || 'No se pudo encolar actualización de agente');
    } finally {
      setUpdatingAgent(false);
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setSyncStep('idle');
    setSyncMode('rescan');
    setSyncCommandId(null);
    setSyncNotice(null);
    syncTimedOutRef.current = false;
    if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
    if (syncSoftTimeoutRef.current) { clearTimeout(syncSoftTimeoutRef.current); syncSoftTimeoutRef.current = null; }
    if (syncHardTimeoutRef.current) { clearTimeout(syncHardTimeoutRef.current); syncHardTimeoutRef.current = null; }
  };

  const handleSyncMenuAction = (action) => {
    setShowSyncActionsMenu(false);
    if (action === 'update_agent') {
      handleUpdateAgentNow();
      return;
    }
    handleSyncNow(action);
  };

  const handleCreateApiKey = async () => {
    if (!companyId) return;
    setCreatingApiKey(true);
    try {
      const keyName = `agent-${form.hostname || form.name || 'server'}-${Date.now()}`;
      const res = await api.createAgentApiKey(companyId, keyName);
      setAgentApiKey(res.raw_key);
      loadAgentApiKeys();
    } catch (e) {
      setApiError(e.message || 'No se pudo crear la API key');
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('¿Revocar esta API key? Los agentes que la usen dejarán de funcionar.')) return;
    try {
      await api.revokeAgentApiKey(companyId, keyId);
      loadAgentApiKeys();
    } catch (e) {
      setApiError(e.message || 'No se pudo revocar la API key');
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('¿Borrar permanentemente esta API key? Esta acción no se puede deshacer.')) return;
    try {
      await api.deleteAgentApiKey(companyId, keyId);
      loadAgentApiKeys();
    } catch (e) {
      setApiError(e.message || 'No se pudo borrar la API key');
    }
  };

  const loadMergeCandidates = useCallback(async () => {
    if (!companyId || !server?.id) return;
    setMergeCandidatesLoading(true);
    setMergeError('');
    try {
      const response = await api.getServers(companyId);
      setMergeCandidates(response.data || []);
    } catch (e) {
      setMergeError(e.message || 'No se pudieron cargar los elementos candidatos');
    } finally {
      setMergeCandidatesLoading(false);
    }
  }, [companyId, server?.id]);

  const loadMergePreview = useCallback(async (targetId) => {
    if (!companyId || !server?.id || !targetId) {
      setMergePreview(null);
      setMergeResolutions({});
      setMergeRelationResolutions({});
      return;
    }

    setMergePreviewLoading(true);
    setMergeError('');
    try {
      const response = await api.previewCiMerge({
        source_type: 'rack_element',
        source_id: server.id,
        target_type: 'rack_element',
        target_id: Number(targetId),
      }, companyId);
      const preview = response.data || null;
      setMergePreview(preview);
      setMergeResolutions(buildDefaultMergeResolutions(preview?.field_preview));
      setMergeRelationResolutions({});
    } catch (e) {
      setMergePreview(null);
      setMergeResolutions({});
      setMergeRelationResolutions({});
      setMergeError(e.message || 'No se pudo generar el preview de fusión');
    } finally {
      setMergePreviewLoading(false);
    }
  }, [companyId, server?.id]);

  useEffect(() => {
    if (showMergePanel) loadMergeCandidates();
  }, [showMergePanel, loadMergeCandidates]);

  // Auto-select the best candidate (first in sorted list) so the preview loads immediately
  useEffect(() => {
    if (filteredMergeCandidates.length > 0 && !mergeTargetId) {
      const bestId = String(filteredMergeCandidates[0].id);
      setMergeTargetId(bestId);
      loadMergePreview(bestId);
    }
  }, [filteredMergeCandidates, mergeTargetId, loadMergePreview]);

  const handleMergeTargetChange = async (event) => {
    const nextTargetId = event.target.value;
    setMergeTargetId(nextTargetId);
    setMergePreview(null);
    setMergeResolutions({});
    setMergeRelationResolutions({});
    if (nextTargetId) {
      await loadMergePreview(nextTargetId);
    }
  };

  const handleMergeResolutionChange = (fieldName, choice) => {
    setMergeResolutions((prev) => ({
      ...prev,
      [fieldName]: choice,
    }));
  };

  const handleMergeIpResolutionChange = (primary) => {
    setMergeRelationResolutions((prev) => ({
      ...prev,
      ip_assignments: {
        type: 'ip_assignments',
        strategy: 'move',
        primary,
      },
    }));
  };

  const handleMergeVirtualIpResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.virtual_ip_memberships) {
        const next = { ...prev };
        delete next.virtual_ip_memberships;
        return next;
      }
      return {
        ...prev,
        virtual_ip_memberships: {
          type: 'virtual_ip_memberships',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergeComponentsResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.components) {
        const next = { ...prev };
        delete next.components;
        return next;
      }
      return {
        ...prev,
        components: {
          type: 'components',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergePowerResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.power_connections) {
        const next = { ...prev };
        delete next.power_connections;
        return next;
      }
      return {
        ...prev,
        power_connections: {
          type: 'power_connections',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergeIncomingSwitchLinksResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.incoming_switch_links) {
        const next = { ...prev };
        delete next.incoming_switch_links;
        return next;
      }
      return {
        ...prev,
        incoming_switch_links: {
          type: 'incoming_switch_links',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergeIncomingPatchLinksResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.incoming_patch_links) {
        const next = { ...prev };
        delete next.incoming_patch_links;
        return next;
      }
      return {
        ...prev,
        incoming_patch_links: {
          type: 'incoming_patch_links',
          strategy: 'move',
        },
      };
    });
  };

  const handleExecuteMerge = async () => {
    if (!companyId || !server?.id || !mergeTargetId || !canExecuteMerge) return;
    const targetLabel = selectedMergeCandidate?.name || mergePreview?.target?.name || `CI ${mergeTargetId}`;
    if (!window.confirm(`Se va a fusionar este CI dentro de "${targetLabel}". El origen quedará archivado como merged. ¿Continuar?`)) {
      return;
    }

    setMergeExecuting(true);
    setMergeError('');
    setMergeResult(null);
    try {
      const response = await api.executeCiMerge({
        source_type: 'rack_element',
        source_id: server.id,
        target_type: 'rack_element',
        target_id: Number(mergeTargetId),
        field_resolutions: mergeResolutions,
        relation_resolutions: Object.values(mergeRelationResolutions),
      }, companyId);
      const targetId = response?.data?.target?.id || Number(mergeTargetId);
      setMergeResult({
        targetId,
        targetLabel,
      });
      await onSave?.();
    } catch (e) {
      setMergeError(e.message || 'No se pudo ejecutar la fusión');
    } finally {
      setMergeExecuting(false);
    }
  };

  const handleOpenMergedTarget = async () => {
    if (!mergeResult?.targetId || !onOpenServer) return;
    try {
      await onOpenServer(mergeResult.targetId);
    } catch (e) {
      setMergeError(e.message || 'No se pudo abrir el CI destino');
    }
  };



  // Unidades del servidor + catálogo de LUNs de la empresa: es lo que permite
  // enlazar cada volumen con su LUN de cabina (y que el diseñador pinte la cadena).
  const refreshVolumes = useCallback(async () => {
    if (!isExisting || !server?.id || !companyId) return;
    try {
      const [luns, vols] = await Promise.all([
        api.getLuns(companyId),
        api.getVolumes(companyId, 'rack_element', server.id),
      ]);
      setCompanyLuns(luns.data || []);
      setVolumeCount((vols.data || []).length);
      // LUNs que publica ESTE equipo (un Windows/Linux con iSCSI Target hace
      // de cabina para otros hosts, sobre todo en entornos Hyper-V).
      setLunCount((luns.data || []).filter(l => l.element_id === server.id).length);
    } catch (_) { /* sin LUNs o sin permisos: la pestaña se ve igual */ }
  }, [isExisting, server?.id, companyId]);

  useEffect(() => { refreshVolumes(); }, [refreshVolumes]);

  const tabAlmacenamiento = { key: 'almacenamiento', label: `Almacenamiento (${volumeCount})` };
  const tabLuns = { key: 'luns', label: `LUNs (${lunCount})` };
  const tabOrganizacion = {
    key: 'organizacion',
    label: `Organización${effectiveRolesCount > 0 ? ` (${effectiveRolesCount})` : ''}`,
  };
  const tabRed = { key: 'red', label: `Red / IP${ipCount > 0 ? ` (${ipCount})` : ''}` };
  const tabAccesos = { key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` };
  const tabMonitorizacion = { key: 'monitoring', label: 'Monitorización' };
  const tabHistorial = { key: 'historial', label: 'Historial' };

  // La ficha clásica conserva sus 12 pestañas tal cual. La variante tarjetas
  // deja 7 visibles y agrupa las de consulta/dependientes de guardado en «Más».
  const tabs = isCards
    ? [
        { key: 'resumen', label: 'General' },
        { key: 'hardware', label: 'Hardware' },
        ...(isExisting ? [tabAlmacenamiento] : []),
        ...(isExisting ? [tabRed] : []),
        tabOrganizacion,
        ...(isExisting ? [tabAccesos] : []),
        ...(isExisting ? [tabMonitorizacion] : []),
      ]
    : [
        { key: 'general', label: 'Identificación' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'hardware', label: 'Hardware' },
        ...(isExisting ? [tabAlmacenamiento] : []),
        ...(isExisting ? [tabLuns] : []),
        { key: 'licencia', label: 'Licencia' },
        tabOrganizacion,
        tabRed,
        { key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        ...(!isNode ? [{ key: 'power', label: '⚡ Alimentación' }] : []),
        ...(isExisting ? [{ key: 'monitoring', label: '📊 Monitorización' }] : []),
        ...(isExisting ? [{ key: 'historial', label: '📋 Historial' }] : []),
      ];

  const tabsMore = isCards
    ? [
        { key: 'licencia', label: 'Licencia' },
        ...(!isNode ? [{ key: 'power', label: 'Alimentación' }] : []),
        ...(isExisting ? [tabHistorial] : []),
        ...(isNode ? [{ key: 'ubicacion', label: 'Ubicación (chasis)' }] : []),
      ]
    : [];
  const tabActualMas = tabsMore.find((tab) => tab.key === activeTab) || null;
  const ENV_LABEL = { prod: 'Producción', pre: 'Pre-producción', dev: 'Desarrollo' };

  if (showDisksPanel) {
    return (
      <ServerDisksPanel
        server={server}
        companyId={companyId}
        variant={isCards ? 'cards' : 'classic'}
        onClose={(newCount) => {
          setShowDisksPanel(false);
          if (typeof newCount === 'number') setDiskCount(newCount);
          // El inventario de discos sincroniza sus volúmenes con la pestaña
          // «Almacenamiento»: refrescar el contador al volver.
          refreshVolumes();
        }}
      />
    );
  }

  if (showComponentsPanel) {
    return (
      <ServerComponentsPanel
        server={server}
        companyId={companyId}
        onClose={() => {
          setShowComponentsPanel(false);
          refreshComponentsCount();
        }}
        onUpdated={refreshComponentsCount}
      />
    );
  }

  if (showUsersPanel) {
    return (
      <SystemUsersPanel
        record={server}
        companyId={companyId}
        getUsers={(serverId, currentCompanyId) => api.getServerUsers(serverId, currentCompanyId)}
        entityLabel="Servidor"
        onClose={() => {
          setShowUsersPanel(false);
          refreshUsersCount();
        }}
      />
    );
  }

  return (
    <>
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Servidores
        </button>
        <DetailCrumb label={form.name} />

        {/* Contador de fichas: a la izquierda, tras la migaja (§3.1). */}
        <FormPager
          items={navItems || []}
          index={navIndex}
          label="Servidor"
          disabled={saving}
          onChange={irANav}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckIcon style={{ width: 15, height: 15 }} /> Guardado
          </span>
        )}
        {ipamNotice && (
          <span style={{ color: '#0891b2', fontSize: '0.78rem', maxWidth: 380, whiteSpace: 'nowrap' }}>
            ⓘ {ipamNotice}
          </span>
        )}
        {(apiError || errors.length > 0) && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError || errors[0]}
          </span>
        )}

        <div className="dv-topbar-actions">
          {!isCards && (
            <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
              ● {currentStatus.label}
            </span>
          )}
          {!isCards && firstScanUi && (
            <span className="dv-status-badge" style={firstScanUi.style} title="Estado del primer escaneo automatico">
              {firstScanUi.label}
            </span>
          )}
          {isExisting && server?.agent_id && (
            <div ref={syncActionsMenuRef} style={{ position: 'relative' }}>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={() => setShowSyncActionsMenu((prev) => !prev)}
                disabled={showSyncModal || syncStep === 'enqueueing' || syncStep === 'pending' || syncStep === 'running' || updatingAgent}
                aria-haspopup="menu"
                aria-expanded={showSyncActionsMenu}
                title="Abrir acciones de sincronización y agente"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <SparklesIcon />
                Sincronizar
                <ChevronDownIcon style={{ width: 16, height: 16 }} />
              </button>
              {showSyncActionsMenu && (
                <div
                  role="menu"
                  aria-label="Acciones de sincronización"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: 280,
                    padding: 8,
                    borderRadius: 12,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-card)',
                    boxShadow: '0 18px 38px rgba(15, 23, 42, 0.18)',
                    zIndex: 40,
                    display: 'grid',
                    gap: 6,
                  }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleSyncMenuAction('rescan')}
                    style={{
                      display: 'grid',
                      gap: 2,
                      textAlign: 'left',
                      padding: '0.7rem 0.8rem',
                      borderRadius: 10,
                      border: '1px solid transparent',
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>Sincronizar solo máquina</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Actualiza nombre, hardware, tarjetas, discos y resto del inventario del servidor.</span>
                  </button>
                  {hypervisorDetection.isDetected && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleSyncMenuAction('hypervisor_sync')}
                      style={{
                        display: 'grid',
                        gap: 2,
                        textAlign: 'left',
                        padding: '0.7rem 0.8rem',
                        borderRadius: 10,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>Sincronizar hipervisor y máquinas virtuales</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Sincroniza el host detectado como {hypervisorDetection.label || 'hipervisor'} y sus VMs en la CMDB.</span>
                    </button>
                  )}
                  {hypervisorDetection.isDetected && server?.agent_id && (
                    <div
                      style={{
                        display: 'grid',
                        gap: 6,
                        padding: '0.7rem 0.8rem',
                        borderRadius: 10,
                        border: '1px solid var(--border)',
                        background: 'rgba(37, 99, 235, 0.06)',
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontWeight: 700,
                          cursor: autoSyncSaving ? 'progress' : 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={autoSyncEnabled}
                          disabled={autoSyncSaving || !editing}
                          onChange={(e) => handleAutoSyncChange({ enabled: e.target.checked })}
                          style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
                        />
                        Sincronización automática
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Cada</span>
                        <select
                          value={autoSync?.minutes == null ? '' : String(autoSync.minutes)}
                          disabled={autoSyncSaving || !autoSyncEnabled || !editing}
                          onChange={(e) => handleAutoSyncChange({
                            minutes: e.target.value === '' ? null : Number(e.target.value),
                          })}
                          style={{
                            padding: '0.3rem 0.4rem',
                            borderRadius: 8,
                            border: '1px solid var(--border)',
                            background: 'var(--bg-card)',
                            color: 'var(--text-primary)',
                            fontSize: '0.8rem',
                          }}
                        >
                          <option value="">
                            Heredar del sistema{autoSync?.minutes == null ? ` (${formatIntervalLabel(autoSyncMinutes)})` : ''}
                          </option>
                          {autoSyncIntervalOptions.map((minutes) => (
                            <option key={minutes} value={String(minutes)}>{formatIntervalLabel(minutes)}</option>
                          ))}
                        </select>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                          {autoSyncSaving
                            ? 'Guardando…'
                            : autoSyncEnabled
                              ? `Sincroniza cada ${formatIntervalLabel(autoSyncMinutes)}`
                              : 'Desactivada: sólo con el botón de arriba'}
                        </span>
                      </div>
                      <span style={{ color: autoSyncError ? '#dc2626' : 'var(--text-secondary)', fontSize: '0.78rem' }}>
                        {autoSyncError || autoSyncLastLabel}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
                        Se ejecuta sola cuando el agente del servidor está vivo; el trabajo lo hace el agente, no la web.
                      </span>
                    </div>
                  )}
                  <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleSyncMenuAction('update_agent')}
                    style={{
                      display: 'grid',
                      gap: 2,
                      textAlign: 'left',
                      padding: '0.7rem 0.8rem',
                      borderRadius: 10,
                      border: '1px solid transparent',
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}><WrenchScrewdriverIcon style={{ width: 16, height: 16 }} />Actualizar agente</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Encola una actualización remota del agente instalado en este servidor.</span>
                    {agentUpdate?.label && (
                      <span
                        title={agentUpdate.title || agentUpdate.label}
                        style={{ color: agentUpdate.color, fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <i style={{ width: 8, height: 8, borderRadius: 999, background: agentUpdate.color, display: 'inline-block' }} />
                        {agentUpdate.label}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          {!editing && onClone && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => onClone(server)} disabled={saving} title="Copiar este servidor con su configuración">
              <DocumentDuplicateIcon />
              Copiar
            </button>
          )}
          {!editing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} title="Editar la ficha">
              <PencilSquareIcon />
              Editar
            </button>
          )}

          {!isCards && editing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          {!isCards && editing && (
            <button
              className="dv-btn dv-btn-primary"
              type="button"
              onClick={handleSubmit}
              disabled={saving || !isDirty}
            >
              {saving ? <span className="dv-spinner" /> : <CheckIcon />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}

          {isExisting && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={handleConvertToStorage}
              disabled={saving}
              title={(server?.element_type || '') === 'storage' ? 'Reclasificar esta cabina como servidor' : 'Reclasificar este elemento como cabina de almacenamiento'}
            >
              <CircleStackIcon />
              {(server?.element_type || '') === 'storage' ? 'A servidor' : 'A cabina'}
            </button>
          )}
          {isExisting && (
            <button
              className="dv-btn dv-btn-danger"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
              title="Eliminar el servidor"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo ficha clásica; en tarjetas los contadores van a chips) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('ubicacion')}
          title="Ir a Ubicación"
        >
          <span className="dv-smart-btn-icon">
            <MapPinIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.85rem' }}>
            {form.location_name || (form.location_id ? '—' : 'Sin ubicación')}
          </span>
          <span className="dv-smart-btn-label">Ubicación</span>
        </button>

        {form.rack_id && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setActiveTab('ubicacion')}
            title="Rack asignado"
          >
            <span className="dv-smart-btn-icon">
              <CubeIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.85rem' }}>
              {form.rack_name || 'Rack'}
              {rangeLabel && <span style={{ fontWeight: 400, fontSize: '0.72rem' }}> {rangeLabel}</span>}
            </span>
            <span className="dv-smart-btn-label">Rack</span>
          </button>
        )}

        {/* Enlace cruzado: un nodo lleva a la ficha de su chasis multinodo */}
        {form.chassis_id && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => navigate(`/infrastructure/chassis?destacar=${form.chassis_id}&abrir=1`)}
            title="Abrir la ficha del chasis multinodo al que pertenece este nodo"
          >
            <span className="dv-smart-btn-icon">
              <ServerStackIcon style={{ width: 18, height: 18, color: '#0d9488' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#0d9488', fontSize: '0.85rem' }}>
              {chassisList.find((c) => String(c.id) === String(form.chassis_id))?.name || `#${form.chassis_id}`}
              {form.chassis_node != null && ` · N${form.chassis_node}`}
            </span>
            <span className="dv-smart-btn-label">Chasis</span>
          </button>
        )}

        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('organizacion')}
          title="Roles detectados / asignados"
        >
          <span className="dv-smart-btn-icon">
            <TagIcon style={{ width: 18, height: 18, color: '#059669' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#059669' }}>
            {effectiveRolesCount}
          </span>
          <span className="dv-smart-btn-label">Roles</span>
        </button>

        {hypervisorDetection.isDetected && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => navigate('/virt/hypervisors')}
            title={`Ir a hypervisores (${hypervisorDetection.label || 'detectado'})`}
            style={{
              borderColor: 'rgba(168,85,247,.35)',
              boxShadow: 'inset 0 0 0 1px rgba(168,85,247,.22)',
            }}
          >
            <span className="dv-smart-btn-icon">
              <WrenchScrewdriverIcon style={{ width: 18, height: 18, color: '#a855f7' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#a855f7', fontSize: '0.8rem' }}>
              {hypervisorDetection.label || 'Hipervisor'}
            </span>
            <span className="dv-smart-btn-label">Hipervisor</span>
          </button>
        )}

        <button className="dv-smart-btn" type="button" title="Entorno">
          <span className="dv-smart-btn-value" style={{ fontSize: '0.8rem' }}>
            {envColors[form.environment]?.label || form.environment}
          </span>
          <span className="dv-smart-btn-label">Entorno</span>
        </button>

        {(form.domain_name != null || form.part_of_domain != null) && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-icon"><BuildingOfficeIcon style={{ width: 18, height: 18, color: form.part_of_domain ? '#0891b2' : '#6b7280' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: form.part_of_domain ? '#0891b2' : '#6b7280', fontSize: '0.8rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.part_of_domain ? (form.domain_name || 'Dominio') : (form.domain_name || 'Grupo de trabajo')}
            </span>
            <span className="dv-smart-btn-label">{form.part_of_domain ? 'Dominio' : 'Workgroup'}</span>
          </button>
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setShowDisksPanel(true)}
            title="Inventario de discos y almacenamiento"
            style={storageAdvisory ? { borderColor: 'rgba(245,158,11,.45)', boxShadow: 'inset 0 0 0 1px rgba(245,158,11,.22)', position: 'relative' } : { position: 'relative' }}
          >
            <span className="dv-smart-btn-icon">
              <CircleStackIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed' }}>
              {diskCount == null ? <span className="dv-spinner" style={{ width: 12, height: 12 }} /> : diskCount}
            </span>
            <span className="dv-smart-btn-label">Discos</span>
            {storageAdvisory && (
              <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
            )}
          </button>
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setShowComponentsPanel(true)}
            title="Componentes de rack (NIC, HBA, RAID, etc.)"
          >
            <span className="dv-smart-btn-value" style={{ color: '#0f766e' }}>
              {componentsCount == null ? <span className="dv-spinner" style={{ width: 12, height: 12 }} /> : (componentsCount || 0)}
            </span>
            <span className="dv-smart-btn-label">Componentes</span>
          </button>
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setShowUsersPanel(true)}
            title="Usuarios detectados por el agente"
          >
            <span className="dv-smart-btn-icon">
              <UserIcon style={{ width: 18, height: 18, color: '#9333ea' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#9333ea' }}>
              {usersCount || 0}
            </span>
            <span className="dv-smart-btn-label">Usuarios</span>
          </button>
        )}

        {isExisting && (
          <DocSmartButton elementType="server" elementId={server.id} elementName={form.name || server.name} companyId={companyId} />
        )}

        {isExisting && (
          <ContactsSmartButton
            companyId={companyId}
            objectType="server"
            objectId={server.id}
            objectName={form.name || server.name}
          />
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setShowMergePanel((value) => !value)}
            title="Fusionar este CI con otro elemento del mismo tipo"
          >
            <span className="dv-smart-btn-icon">
              <DocumentDuplicateIcon style={{ width: 18, height: 18, color: '#b45309' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#b45309', fontSize: '0.8rem' }}>
              {mergePreview?.blocked ? 'Bloqueado' : 'Preview'}
            </span>
            <span className="dv-smart-btn-label">Fusionar</span>
          </button>
        )}

        {isExisting && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => agentStatus === 'unknown' ? setShowAgentSetup(v => !v) : setActiveTab('monitoring')}
            title={agentLastSeen ? `Última conexión: ${new Date(agentLastSeen).toLocaleString('es-ES')}` : 'Click para configurar agente'}
            style={{ position: 'relative' }}
          >
            <span className="dv-smart-btn-icon">
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                background: agentStatus === 'online' ? '#22c55e' : agentStatus === 'offline' ? '#ef4444' : '#6b7280',
                boxShadow: agentStatus === 'online' ? '0 0 6px rgba(34,197,94,.6)' : 'none',
              }} />
            </span>
            <span className="dv-smart-btn-value" style={{
              color: agentStatus === 'online' ? '#22c55e' : agentStatus === 'offline' ? '#ef4444' : '#6b7280',
              fontSize: '0.8rem',
            }}>
              {agentStatus === 'online' ? 'Online' : agentStatus === 'offline' ? 'Offline' : 'Sin agente'}
            </span>
            <span className="dv-smart-btn-label">Agente</span>
          </button>
        )}
      </div>
      )}

      {showMergePanel && isExisting && (
        <div style={{
          margin: '0 1.5rem 0.75rem',
          padding: '1rem 1.25rem',
          borderRadius: 10,
          border: '1px solid rgba(180,83,9,.25)',
          background: 'linear-gradient(135deg, rgba(245,158,11,.14), rgba(251,191,36,.06))',
          color: 'var(--text-primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b45309', fontWeight: 700, marginBottom: 2 }}>
                Fusión manual de duplicados
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                Selecciona el CI destino, revisa el preview y confirma solo si no hay bloqueos manuales.
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Esta primera versión mueve relaciones simples automáticamente y bloquea red, componentes y casos ambiguos.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMergePanel(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            >✕</button>
          </div>

          {mergeError && (
            <div style={{
              marginBottom: 12,
              padding: '0.7rem 0.9rem',
              borderRadius: 8,
              background: 'rgba(220,38,38,.08)',
              border: '1px solid rgba(220,38,38,.2)',
              color: '#b91c1c',
              fontSize: '0.85rem',
            }}>
              {mergeError}
            </div>
          )}

          {mergeResult && (
            <div style={{
              marginBottom: 12,
              padding: '0.8rem 0.95rem',
              borderRadius: 8,
              background: 'rgba(16,185,129,.08)',
              border: '1px solid rgba(16,185,129,.2)',
              color: '#047857',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontWeight: 700 }}>Fusión completada</div>
                <div style={{ fontSize: '0.84rem' }}>
                  El origen ha quedado archivado como `merged` y el CI superviviente es {mergeResult.targetLabel}.
                </div>
              </div>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={handleOpenMergedTarget}
              >
                Abrir CI destino
              </button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Buscar destino
                </label>
                <input
                  type="text"
                  value={mergeSearch}
                  onChange={(event) => setMergeSearch(event.target.value)}
                  placeholder="Nombre, hostname, IP, serie..."
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '0.55rem 0.7rem',
                    fontSize: '0.84rem',
                    background: 'var(--bg-secondary, #1e1e2e)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  CI destino
                </label>
                <select
                  value={mergeTargetId}
                  onChange={handleMergeTargetChange}
                  size={Math.min(Math.max(filteredMergeCandidates.length, 2), 8)}
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '0.35rem',
                    fontSize: '0.84rem',
                    background: 'var(--bg-secondary, #1e1e2e)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {mergeCandidatesLoading ? (
                    <option value="">Cargando candidatos...</option>
                  ) : filteredMergeCandidates.length === 0 ? (
                    <option value="">No hay candidatos visibles</option>
                  ) : (
                    filteredMergeCandidates.map((candidate) => {
                      const score = buildMergeCandidateScore(server, candidate);
                      const suffix = [candidate.hostname, candidate.ip_address].filter(Boolean).join(' · ');
                      return (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name || `CI ${candidate.id}`}{suffix ? ` · ${suffix}` : ''}{score > 0 ? ` · score ${score}` : ''}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              {selectedMergeCandidate && (
                <div style={{
                  padding: '0.8rem 0.9rem',
                  borderRadius: 8,
                  background: 'var(--bg-tertiary, rgba(255,255,255,.08))',
                  border: '1px solid rgba(180,83,9,.18)',
                  fontSize: '0.82rem',
                  display: 'grid',
                  gap: 4,
                }}>
                  <div style={{ fontWeight: 700 }}>{selectedMergeCandidate.name || `CI ${selectedMergeCandidate.id}`}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{selectedMergeCandidate.hostname || 'Sin hostname'}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{selectedMergeCandidate.ip_address || 'Sin IP principal'}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>
                    {selectedMergeCandidate.location_name || 'Sin ubicación'}{selectedMergeCandidate.rack_name ? ` · ${selectedMergeCandidate.rack_name}` : ''}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={loadMergeCandidates}
                  disabled={mergeCandidatesLoading}
                >
                  {mergeCandidatesLoading ? 'Recargando...' : 'Recargar lista'}
                </button>
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => loadMergePreview(mergeTargetId)}
                  disabled={!mergeTargetId || mergePreviewLoading}
                >
                  {mergePreviewLoading ? 'Calculando...' : 'Refrescar preview'}
                </button>
              </div>
            </div>

            <div style={{
              minHeight: 160,
              maxHeight: 'calc(100vh - 260px)',
              overflowY: 'auto',
              padding: '0.9rem 1rem',
              borderRadius: 8,
              background: 'var(--bg-tertiary, rgba(255,255,255,.08))',
              border: '1px solid rgba(180,83,9,.16)',
            }}>
              {!mergeTargetId ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                  Selecciona un CI destino para generar el preview de fusión.
                </div>
              ) : mergePreviewLoading ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                  Calculando diferencias y relaciones a mover...
                </div>
              ) : mergePreview ? (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                        Preview
                      </div>
                      <div style={{ fontWeight: 700 }}>
                        {mergePreview.source?.name || `CI ${mergePreview.source?.id}`} → {mergePreview.target?.name || `CI ${mergePreview.target?.id}`}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.35rem 0.7rem',
                      borderRadius: 999,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      background: canExecuteMerge ? 'rgba(16,185,129,.12)' : 'rgba(220,38,38,.1)',
                      color: canExecuteMerge ? '#047857' : '#b91c1c',
                    }}>
                      {canExecuteMerge ? 'Listo para ejecutar' : 'Bloqueado'}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Campos a completar</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0369a1' }}>{mergePreview.field_summary?.take_source || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Conflictos de campo</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#b45309' }}>{mergePreview.field_summary?.conflicts || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Movimientos automáticos</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#047857' }}>{mergePreview.relation_summary?.automatic_moves || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Bloqueos</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: mergeUnresolvedBlocks.length ? '#b91c1c' : '#6b7280' }}>{mergeUnresolvedBlocks.length}</div>
                    </div>
                  </div>

                  {mergeConflictFields.length > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Conflictos de identidad</div>
                      {mergeConflictFields.map((item) => (
                        <div key={item.field} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem 0.9rem', display: 'grid', gap: 8 }}>
                          <div style={{ fontWeight: 700 }}>{item.label}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                            <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.6rem 0.7rem', cursor: 'pointer', background: mergeResolutions[item.field] === 'target' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <input
                                  type="radio"
                                  name={`merge-${item.field}`}
                                  checked={mergeResolutions[item.field] === 'target'}
                                  onChange={() => handleMergeResolutionChange(item.field, 'target')}
                                />
                                <span style={{ fontWeight: 700 }}>Conservar destino</span>
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeValue(item.target_value)}</div>
                            </label>
                            <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.6rem 0.7rem', cursor: 'pointer', background: mergeResolutions[item.field] === 'source' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <input
                                  type="radio"
                                  name={`merge-${item.field}`}
                                  checked={mergeResolutions[item.field] === 'source'}
                                  onChange={() => handleMergeResolutionChange(item.field, 'source')}
                                />
                                <span style={{ fontWeight: 700 }}>Tomar origen</span>
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeValue(item.source_value)}</div>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Relaciones afectadas</div>
                    {mergeMovableRelations.length === 0 ? (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.84rem' }}>No hay relaciones activas en el origen para mover.</div>
                    ) : (
                      mergeMovableRelations.map((item) => (
                        <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '0.65rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)', border: '1px solid var(--border)' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{item.label}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                              Origen: {item.source_count} · Destino: {item.target_count}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: item.action === 'move' ? '#047857' : '#b45309',
                          }}>
                            {item.action === 'move' ? 'Automático' : 'Revisión manual'}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {mergeIpAssignmentsRelation && mergeIpAssignmentsRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de IPs</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          El backend ya puede mover las IPs del origen al destino si eliges qué IP debe quedar como primaria tras la fusión.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>IPs del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIpAssignmentsRelation.details?.source_ips)}</div>
                            <div style={{ color: '#b45309', fontSize: '0.78rem', marginTop: 6 }}>
                              Primaria actual: {formatMergeIpList(mergeIpAssignmentsRelation.details?.source_primary)}
                            </div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>IPs del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIpAssignmentsRelation.details?.target_ips)}</div>
                            <div style={{ color: '#0369a1', fontSize: '0.78rem', marginTop: 6 }}>
                              Primaria actual: {formatMergeIpList(mergeIpAssignmentsRelation.details?.target_primary)}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
                          <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.ip_assignments?.primary === 'target' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                              <input
                                type="radio"
                                name="merge-ip-primary"
                                checked={mergeRelationResolutions.ip_assignments?.primary === 'target'}
                                onChange={() => handleMergeIpResolutionChange('target')}
                              />
                              <span style={{ fontWeight: 700 }}>Conservar primaria del destino</span>
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                              Se moverán las IPs del origen, pero seguirá mandando la IP principal ya existente en el destino.
                            </div>
                          </label>
                          <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.ip_assignments?.primary === 'source' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                              <input
                                type="radio"
                                name="merge-ip-primary"
                                checked={mergeRelationResolutions.ip_assignments?.primary === 'source'}
                                onChange={() => handleMergeIpResolutionChange('source')}
                              />
                              <span style={{ fontWeight: 700 }}>Usar como primaria la del origen</span>
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                              Tras mover las IPs, la primaria pasará a ser una IP procedente del origen si existe.
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {mergeVirtualIpRelation && mergeVirtualIpRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de grupos de IP virtual</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Grupos del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeVirtualIpRelation.details?.source_groups)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Grupos del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeVirtualIpRelation.details?.target_groups)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.virtual_ip_memberships ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.virtual_ip_memberships)}
                              onChange={handleMergeVirtualIpResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Mover membresías de IP virtual al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Si el destino ya pertenece a alguno de esos grupos, el backend deduplicará esa membresía en vez de crearla dos veces.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergeComponentsRelation && mergeComponentsRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de componentes</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción mueve los componentes físicos del origen al destino junto con sus puertos asociados. Si ambos CIs usan el mismo slot, el sistema lo mantendrá y te lo muestra para revisión antes de confirmar.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Slots del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeComponentsRelation.details?.source_slots)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Slots del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeComponentsRelation.details?.target_slots)}</div>
                          </div>
                        </div>
                        {Array.isArray(mergeComponentsRelation.details?.overlapping_slots) && mergeComponentsRelation.details.overlapping_slots.length > 0 && (
                          <div style={{ border: '1px solid rgba(220,38,38,.15)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(220,38,38,.05)', color: '#991b1b', fontSize: '0.82rem' }}>
                            Slots con solape detectado: {formatMergeIpList(mergeComponentsRelation.details.overlapping_slots)}
                          </div>
                        )}
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.components ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.components)}
                              onChange={handleMergeComponentsResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Mover componentes y puertos asociados al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            El merge no renombra ni reordena slots; solo reasigna la pertenencia del componente al CI destino.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergePowerRelation && mergePowerRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de alimentación</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna al CI destino las conexiones eléctricas del origen. Cada salida de PDU sigue siendo única; solo cambia el consumidor asociado a esa alimentación.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Alimentación del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergePowerRelation.details?.source_outlets)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Alimentación del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergePowerRelation.details?.target_outlets)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.power_connections ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.power_connections)}
                              onChange={handleMergePowerResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Mover conexiones eléctricas al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Útil cuando el origen es un duplicado lógico y quieres conservar la misma alimentación física sobre el CI superviviente.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergeIncomingSwitchLinksRelation && mergeIncomingSwitchLinksRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de enlaces entrantes desde switches</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna al CI destino los puertos de switch que hoy apuntan al origen. No modifica el cableado físico representado en otras tablas; solo actualiza el elemento lógico de destino del enlace.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que apuntan al origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIncomingSwitchLinksRelation.details?.source_links)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que ya apuntan al destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIncomingSwitchLinksRelation.details?.target_links)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.incoming_switch_links ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.incoming_switch_links)}
                              onChange={handleMergeIncomingSwitchLinksResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Reasignar estos enlaces al CI destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Útil cuando el origen es un duplicado lógico y quieres que los puertos de switch sigan señalando al CI superviviente.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergeIncomingPatchLinksRelation && mergeIncomingPatchLinksRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de enlaces entrantes desde patch panels</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna al CI destino los puertos de patch panel que hoy apuntan al origen. No modifica el cableado físico; solo actualiza la referencia lógica del elemento conectado.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que apuntan al origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIncomingPatchLinksRelation.details?.source_links)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que ya apuntan al destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeIpList(mergeIncomingPatchLinksRelation.details?.target_links)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.incoming_patch_links ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.incoming_patch_links)}
                              onChange={handleMergeIncomingPatchLinksResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Reasignar estos enlaces al CI destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Útil cuando el origen es un duplicado lógico y quieres que el patch panel siga señalando al CI superviviente.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {Array.isArray(mergeUnresolvedBlocks) && mergeUnresolvedBlocks.length > 0 && (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#b91c1c' }}>Bloqueos detectados</div>
                      {mergeUnresolvedBlocks.map((block) => (
                        <div key={block.key} style={{ padding: '0.75rem 0.85rem', borderRadius: 8, background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.15)' }}>
                          <div style={{ fontWeight: 700, color: '#991b1b' }}>{block.label}</div>
                          <div style={{ color: '#b91c1c', fontSize: '0.82rem' }}>{block.reason}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4 }}>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      Si no cambias un conflicto de campo, el backend conserva el valor del destino.
                    </div>
                    <button
                      className="dv-btn dv-btn-primary"
                      type="button"
                      onClick={handleExecuteMerge}
                      disabled={!canExecuteMerge || mergeExecuting}
                    >
                      {mergeExecuting ? 'Fusionando...' : 'Ejecutar fusión'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                  No se ha podido cargar el preview todavía.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Agent Setup Panel ── */}
      {showAgentSetup && (
        <div style={{
          margin: '0 1.5rem 0.5rem',
          padding: '1rem 1.25rem',
          borderRadius: 10,
          border: '1px solid rgba(var(--accent-rgb),.3)',
          background: 'linear-gradient(135deg, rgba(var(--accent-rgb),.08), rgba(var(--accent-rgb),.04))',
          color: 'var(--text-primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 700, marginBottom: 2 }}>
                Instalación de agente
              </div>
              <div style={{ fontSize: '0.92rem', fontWeight: 600 }}>
                Genera una API key de empresa para instalar el agente CMDB en este servidor
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Esta acción no vincula automáticamente la API key a este CI. La asociación del inventario se resuelve por fingerprints o por credencial del agente.
              </div>
            </div>
            <button type="button" onClick={() => { setShowAgentSetup(false); setAgentApiKey(null); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>

          {!agentApiKey ? (
            <button
              type="button"
              onClick={handleCreateApiKey}
              disabled={creatingApiKey}
              style={{
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                color: '#fff', border: 'none', borderRadius: 6, padding: '0.45rem 1.1rem',
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              }}
            >
              {creatingApiKey ? 'Creando…' : '🔑 Generar API key de empresa'}
            </button>
          ) : (
            <div>
              <div style={{ marginBottom: 8 }}>
                <small style={{ color: '#d97706', fontWeight: 600 }}>⚠ Copia esta clave ahora — no se mostrará de nuevo</small>
              </div>
              <div style={{
                background: 'var(--slate-200)', borderRadius: 6, padding: '0.6rem 0.8rem',
                fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--accent)',
                wordBreak: 'break-all', marginBottom: 10, position: 'relative',
              }}>
                {agentApiKey}
                <button type="button"
                  onClick={() => navigator.clipboard.writeText(agentApiKey)}
                  style={{ position: 'absolute', top: 6, right: 6, background: 'var(--slate-300)', border: 'none', borderRadius: 4, color: 'var(--text-primary)', cursor: 'pointer', padding: '2px 8px', fontSize: '0.75rem' }}
                >Copiar</button>
              </div>
              <div style={{ marginBottom: 4 }}>
                <small style={{ color: 'var(--text-secondary)' }}>Comando de instalación (ejecutar en el servidor destino):</small>
              </div>
              <div style={{
                background: 'var(--slate-200)', borderRadius: 6, padding: '0.6rem 0.8rem',
                fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: '#059669',
                whiteSpace: 'pre-wrap', lineHeight: 1.6, position: 'relative',
              }}>
                {`python agent.py daemon --url <CMDB_URL> --key ${agentApiKey}`}
                <button type="button"
                  onClick={() => navigator.clipboard.writeText(`python agent.py daemon --url <CMDB_URL> --key ${agentApiKey}`)}
                  style={{ position: 'absolute', top: 6, right: 6, background: 'var(--slate-300)', border: 'none', borderRadius: 4, color: 'var(--text-primary)', cursor: 'pointer', padding: '2px 8px', fontSize: '0.75rem' }}
                >Copiar</button>
              </div>
            </div>
          )}

          {/* ── API Keys table ── */}
          {agentApiKeys.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
                API Keys de la empresa
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>Estado</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>Nombre</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>Prefix</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>Último uso</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>Creada</th>
                    <th style={{ padding: '4px 8px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {agentApiKeys.map(k => (
                    <tr key={k.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 8px' }}>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: k.is_active ? '#22c55e' : '#ef4444', marginRight: 6, verticalAlign: 'middle',
                        }} />
                        <span style={{ fontSize: '0.78rem' }}>{k.is_active ? 'Activa' : 'Revocada'}</span>
                      </td>
                      <td style={{ padding: '5px 8px', fontSize: '0.78rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.name}</td>
                      <td style={{ padding: '5px 8px' }}><code style={{ fontSize: '0.75rem', color: 'var(--accent)' }}>{k.key_prefix}…</code></td>
                      <td style={{ padding: '5px 8px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</td>
                      <td style={{ padding: '5px 8px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        {k.is_active && (
                          <button type="button" onClick={() => handleRevokeApiKey(k.id)}
                            style={{ background: 'none', border: '1px solid rgba(239,68,68,.4)', borderRadius: 4, color: '#f87171', cursor: 'pointer', padding: '2px 8px', fontSize: '0.72rem', marginRight: 4 }}
                          >Revocar</button>
                        )}
                        <button type="button" onClick={() => handleDeleteApiKey(k.id)}
                          title="Borrar permanentemente"
                          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 6px', fontSize: '0.72rem' }}
                        >🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {storageAdvisory && !storageAdvisoryDismissed && (
        <div style={{
          margin: '0 1.5rem 1rem',
          padding: '0.9rem 1rem',
          borderRadius: 12,
          border: '1px solid rgba(245,158,11,.35)',
          background: 'linear-gradient(135deg, rgba(245,158,11,.12), rgba(245,158,11,.04))',
          color: 'var(--text-primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#d97706', fontWeight: 700, marginBottom: 4 }}>
                {getStorageAdvisoryEyebrow(storageAdvisory)}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4 }}>{storageAdvisory.title}</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>{storageAdvisory.message}</div>
              {Array.isArray(storageAdvisory.instructions) && storageAdvisory.instructions.length > 0 && (
                <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {storageAdvisory.instructions.map((step, index) => (
                    <div key={`${index}-${step}`}>{index + 1}. {step}</div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setShowDisksPanel(true)}>
                <CircleStackIcon /> Revisar discos
              </button>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={() => setStorageAdvisoryDismissed(true)}
                title="Oculta el aviso mientras esta ficha está abierta. Volverá a salir al reabrir el servidor si no se ha resuelto."
              >
                Omitir ahora
              </button>
            </div>
          </div>
        </div>
      )}

      {setupWarningsVisibles.length > 0 && (
        <div style={{
          margin: '0 1.5rem 1rem',
          padding: '0.9rem 1rem',
          borderRadius: 12,
          border: '1px solid rgba(245,158,11,.35)',
          background: 'linear-gradient(135deg, rgba(245,158,11,.12), rgba(245,158,11,.04))',
          color: 'var(--text-primary)',
        }}>
          <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#d97706', fontWeight: 700, marginBottom: 6 }}>
            Checklist de configuración
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {setupWarningsVisibles.map((warning) => (
              <div key={warning.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{warning.title}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{warning.message}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="dv-btn dv-btn-secondary"
                    onClick={() => setSetupHiddenNow((prev) => [...prev, warning.key])}
                    title="El aviso desaparece ahora y vuelve a salir la próxima vez que abras el servidor"
                  >
                    Ocultar ahora
                  </button>
                  <button
                    type="button"
                    className="dv-btn dv-btn-secondary"
                    onClick={() => toggleSetupOmit(warning.key)}
                    title="No volver a mostrar este aviso para este servidor"
                  >
                    No volver a mostrar
                  </button>
                </div>
              </div>
            ))}
          </div>
          {avisosOcultosCount > 0 && (
            <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {avisosOcultosCount} aviso{avisosOcultosCount === 1 ? '' : 's'} oculto{avisosOcultosCount === 1 ? '' : 's'} ·{' '}
              <button type="button" className="sf2-link" onClick={restaurarAvisos}>
                Volver a mostrarlo{avisosOcultosCount === 1 ? '' : 's'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Con todos los avisos ocultos el bloque desaparece; queda solo este
          enlace discreto para recuperarlos. */}
      {setupWarningsVisibles.length === 0 && avisosOcultosCount > 0 && (
        <div style={{ margin: '0 1.5rem 1rem' }}>
          <button type="button" className="sf2-link" onClick={restaurarAvisos}>
            Mostrar avisos de configuración ({avisosOcultosCount})
          </button>
        </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        {/* Main panel */}
        <form className={isCards ? 'dv-main sf2-main' : 'dv-main'} onSubmit={handleSubmit}>
          {/* Cabecera + pestañas. En la variante tarjetas van juntas dentro de
              un contenedor sticky para que no se pierdan al hacer scroll; en la
              clásica el envoltorio es `display: contents` y no existe. */}
          <div className={isCards ? 'sf2-headarea' : 'sf2-plain'}>
            {/* Record title */}
            {isCards ? (
              <ServerFormCardsHeader
                form={form}
                rangeLabel={rangeLabel}
                chassisName={chassisList.find((c) => String(c.id) === String(form.chassis_id))?.name || ''}
                rolesCount={effectiveRolesCount}
                disksCount={diskCount || 0}
                nicsCount={nicsCount}
                ipsCount={ipCount}
                accessCount={accessCount}
                usersCount={usersCount || 0}
                lunsCount={lunCount || 0}
                docButton={isExisting ? (
                  <DocSmartButton
                    elementType="server"
                    elementId={server.id}
                    elementName={form.name || server.name}
                    companyId={companyId}
                    variant="chip"
                  />
                ) : null}
                agentStatus={agentStatus}
                agentLastSeen={agentLastSeen}
                agentUpdate={agentUpdate}
                firstScanUi={firstScanUi}
                statusLabel={currentStatus.label}
                statusBg={currentStatus.bg}
                statusColor={currentStatus.color}
                envLabel={ENV_LABEL[form.environment] || 'Producción'}
                isExisting={isExisting}
                onGo={(key) => setActiveTab(key)}
                onOpenDisks={() => setShowDisksPanel(true)}
                onOpenUsers={() => setShowUsersPanel(true)}
                editing={editing}
                saving={saving}
                isDirty={isDirty || nameDirty}
                onEdit={() => setEditing(true)}
                onSave={handleSubmit}
                onDiscard={handleDiscard}
                onTitleChange={(v) => { setForm(prev => ({ ...prev, name: v })); setIsDirty(true); }}
                titleDirty={nameDirty}
                onTitleCommit={() => handleSubmit(null, { force: true })}
                onTitleReset={handleDiscard}
              />
            ) : (
              <input
                className="dv-record-title"
                name="name"
                value={form.name}
                onChange={handleChange} disabled={!editing}
                placeholder="Nombre del servidor…"
                required
              />
            )}

            {/* Tabs */}
            {isCards ? (
              <div className="sf2-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
                {tabsMore.length > 0 && (
                  <div className="sf2-more" ref={tabsMenuRef}>
                    <button
                      type="button"
                      className="sf2-more-btn"
                      aria-haspopup="menu"
                      aria-expanded={showTabsMenu}
                      onClick={() => setShowTabsMenu((prev) => !prev)}
                    >
                      {tabActualMas ? tabActualMas.label : 'Más'} ▾
                    </button>
                    {showTabsMenu && (
                      <div className="sf2-more-menu" role="menu">
                        {tabsMore.map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            role="menuitem"
                            aria-current={activeTab === tab.key ? 'true' : undefined}
                            onClick={() => {
                              setActiveTab(tab.key);
                              setShowTabsMenu(false);
                            }}
                          >
                            <span>{tab.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="dv-tabs-nav">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Zona desplazable. En tarjetas la cabecera y las pestañas viven
              FUERA de este contenedor, así que no pueden quedar tapadas ni
              dejar ver contenido por detrás. En la clásica el envoltorio es
              `display: contents` y no existe. */}
          <div className={isCards ? 'sf2-scroll' : 'sf2-plain'}>

          {/* ── Tab: Resumen (solo variante tarjetas) ── */}
          {isCards && activeTab === 'resumen' && (
            <div className="dv-tab-pane">
              <ServerFormSummary
                form={form}
                editing={editing}
                onChange={handleChange}
                onLocationChange={handleLocationChange}
                onRackChange={handleRackChange}
                onGo={(key) => setActiveTab(key)}
                locations={locations}
                racks={filteredRacks}
                isNode={isNode}
                rangeLabel={rangeLabel}
                chassisName={chassisList.find((c) => String(c.id) === String(form.chassis_id))?.name || ''}
                hasBmcComponent={hasBmcComponent}
                bmcIp={bmcIp}
                hypervisorLabel={hypervisorDetection.label || ''}
                serverId={server?.id}
                companyId={companyId}
                hasAgent={Boolean(server?.agent_id)}
                manufacturerOptions={manufacturerOptions}
                modelOptions={filteredModelOptions}
                onManufacturerChange={handleManufacturerChange}
                onCreateManufacturer={async (name) => {
                  handleManufacturerChange(name);
                  await saveManufacturerToCatalog(name);
                }}
                onCreateModel={async (name) => {
                  setField('model', name);
                  await saveModelToCatalog(name);
                }}
                onSuggestName={handleSuggest}
                suggestingName={suggesting}
                counts={{
                  roles: effectiveRolesCount,
                  disks: diskCount,
                  components: componentsCount,
                  users: usersCount,
                  luns: lunCount,
                  ips: ipCount,
                  access: accessCount,
                }}
              />
            </div>
          )}

          {/* ── Tab: Identificación ── */}
          {!isCards && activeTab === 'general' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Datos del servidor</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Nombre <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange} disabled={!editing}
                        className="form-control"
                        placeholder="srv-app-01"
                        required
                        autoFocus={!isExisting}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleSuggest}
                        disabled={suggesting}
                        title="Sugerir nombre automático"
                      >
                        {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Hostname</label>
                    <input
                      name="hostname"
                      value={form.hostname}
                      onChange={handleChange} disabled={!editing}
                      className="form-control"
                      placeholder="srv-app-01.example.com"
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label" title="IP de gestión fuera de banda (iLO, iDRAC, IPMI…)">IP BMC / iLO</label>
                    <input
                      name="ip_address"
                      value={hasBmcComponent ? (bmcIp || form.ip_address || '') : form.ip_address}
                      onChange={handleChange}
                      disabled={!editing}
                      readOnly={hasBmcComponent}
                      className="form-control"
                      placeholder={hasBmcComponent ? 'Se asigna en Red / IP' : '10.0.0.1'}
                      title={hasBmcComponent ? 'La IP de la iLO se gestiona desde la pestaña Red / IP (IPAM)' : undefined}
                      style={hasBmcComponent ? { background: 'var(--bg-toolbar)', cursor: 'not-allowed' } : undefined}
                    />
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {hasBmcComponent ? (
                        <>
                          <span style={{ color: '#16a34a', fontWeight: 600 }}>
                            ✔ Interfaz BMC creada · IP gestionada en Red / IP
                          </span>
                          {bmcIpLoading ? (
                            <span>Leyendo la IP de Red / IP…</span>
                          ) : bmcIp ? (
                            <span>🔒 {bmcIp} (asignada a la interfaz BMC / iLO)</span>
                          ) : form.ip_address ? (
                            <span style={{ color: '#d97706' }}>
                              ⚠ Sin asignación en Red / IP; se muestra la última IP guardada.
                            </span>
                          ) : (
                            <span style={{ color: '#d97706' }}>Sin IP asignada en Red / IP.</span>
                          )}
                          <button
                            type="button"
                            onClick={() => setActiveTab('red')}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6366f1', fontSize: '0.7rem', fontWeight: 600, textDecoration: 'underline', textAlign: 'left' }}
                          >
                            Ir a Red / IP →
                          </button>
                        </>
                      ) : server?.id ? (
                        <button
                          type="button"
                          disabled={promotingBmc}
                          onClick={async () => {
                            setPromotingBmc(true);
                            try {
                              await api.promoteBmc(server.id, companyId);
                              await refreshComponentsCount();
                            } catch (e) {
                              alert('Error: ' + e.message);
                            } finally {
                              setPromotingBmc(false);
                            }
                          }}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6366f1', fontSize: '0.7rem', fontWeight: 600, textDecoration: 'underline', textAlign: 'left' }}
                        >
                          {promotingBmc ? 'Creando…' : '🔌 Crear interfaz cableable'}
                        </button>
                      ) : (
                        <span>Campo manual · guarda primero para crear interfaz</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sistema operativo</label>
                    <input
                      name="os"
                      value={form.os}
                      onChange={handleChange} disabled={!editing}
                      className="form-control"
                      placeholder="Ubuntu 22.04 / Windows Server 2022"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Entorno</label>
                    <select name="environment" value={form.environment} onChange={handleChange} disabled={!editing} className="form-select">
                      <option value="prod">🟢 Producción</option>
                      <option value="pre">🟡 Pre-producción</option>
                      <option value="dev">🔵 Desarrollo</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} disabled={!editing} className="form-select">
                      <option value="active">✅ Activo</option>
                      <option value="maintenance">🔧 Mantenimiento</option>
                      <option value="decom">🚫 Decom</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Ubicación ── */}
          {activeTab === 'ubicacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  {/* Factor de forma — siempre visible */}
                  <div className="col-md-4">
                    <label className="form-label">Factor de forma</label>
                    <select name="form_factor" value={formFactor} onChange={handleChange} disabled={!editing} className="form-select">
                      <option value="rack">🗄️ Rack</option>
                      <option value="tower">🖥️ Torre</option>
                      <option value="node">🧩 Nodo (chasis multinodo)</option>
                    </select>
                  </div>

                  {/* ── Modo Nodo → chasis + nº de nodo ── */}
                  {isNode && (
                    <>
                      <div className="col-md-5">
                        <label className="form-label">Chasis <span className="text-danger">*</span></label>
                        <select
                          value={form.chassis_id || ''}
                          onChange={e => {
                            const cid = e.target.value ? parseInt(e.target.value, 10) : '';
                            const ch  = chassisList.find(c => c.id === cid);
                            setForm(prev => ({
                              ...prev,
                              chassis_id:  cid,
                              chassis_node: prev.chassis_node || '',
                              // heredar ubicación / rack del chasis
                              location_id: ch?.location_id || prev.location_id || '',
                              rack_id:     ch?.rack_id     || '',
                            }));
                            setIsDirty(true);
                          }}
                          className="form-select"
                        >
                          <option value="">— Selecciona chasis —</option>
                          {chassisList.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.manufacturer ? ` · ${c.manufacturer}` : ''}{c.model ? ` ${c.model}` : ''}
                              {` (${c.node_count || 0}/${c.total_node_slots || '?'} nodos)`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Nº de nodo</label>
                        <input
                          type="number"
                          min="1"
                          max={chassisList.find(c => String(c.id) === String(form.chassis_id))?.total_node_slots || 128}
                          value={form.chassis_node || ''}
                          onChange={e => setField('chassis_node', e.target.value ? parseInt(e.target.value, 10) : '')}
                          className="form-control"
                          placeholder="ej. 1"
                        />
                        <div className="form-text">Posición física dentro del chasis</div>
                      </div>
                      {/* Info del chasis seleccionado */}
                      {form.chassis_id && (() => {
                        const ch = chassisList.find(c => String(c.id) === String(form.chassis_id));
                        return ch ? (
                          <div className="col-12">
                            <div style={{ background: 'var(--bg-secondary, #f0f2f5)', borderRadius: 6, padding: '8px 14px', fontSize: 13, color: '#555' }}>
                              📍 {ch.location_name || 'Sin ubicación'}
                              {ch.rack_name ? ` › ${ch.rack_name}` : ''}
                              {ch.chassis_type ? ` · ${ch.chassis_type}` : ''}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </>
                  )}

                  {/* ── Modo Rack / Torre → campos habituales ── */}
                  {!isNode && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label">Ubicación <span className="text-danger">*</span></label>
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => handleLocationChange({ target: { value: id } })}
                          placeholder="— Selecciona ubicación —"
                          disabled={!editing}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Rack <span className="text-muted" style={{ fontSize: '0.75rem' }}>(opcional)</span></label>
                        <select
                          name="rack_id"
                          value={form.rack_id}
                          onChange={handleRackChange}
                          className="form-select"
                          disabled={!editing || filteredRacks.length === 0}
                        >
                          <option value="">— Sin rack —</option>
                          {filteredRacks.map(rack => (
                            <option key={rack.id} value={rack.id}>
                              {rack.name || rack.code}{rack.code && rack.name ? ` (${rack.code})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      {showSizeU && (
                        <div className="col-md-3">
                          <label className="form-label">Altura (U)</label>
                          <input
                            type="number" min="1" step="1"
                            name="size_u"
                            value={form.size_u}
                            onChange={handleChange} disabled={!editing}
                            className="form-control"
                            required
                          />
                        </div>
                      )}
                      <div className="col-md-3">
                        <label className="form-label">
                          Posición U <span className="text-muted" style={{ fontSize: '0.75rem' }}>(opcional)</span>
                        </label>
                        <input
                          type="number" min="1"
                          name="position_u"
                          value={form.position_u || ''}
                          onChange={e => setField('position_u', e.target.value ? parseInt(e.target.value, 10) : null)}
                          className="form-control"
                          placeholder="ej. 10"
                        />
                        {rangeLabel && (
                          <div className="form-text">
                            Ocupará {rangeLabel}{rackHeight ? ` · Rack ${rackHeight}U` : ''}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {isCards && activeTab === 'almacenamiento' && isExisting && (
            <div className="dv-tab-pane">
              <ServerCardsStorage
                key={`storage-${diskCount}-${volumeCount}`}
                serverId={server.id}
                companyId={companyId}
                onOpenDisks={() => setShowDisksPanel(true)}
              />
              <div className="sf2-card" style={{ marginTop: 16 }}>
                <h3>Volúmenes del servidor</h3>
                <StorageVolumesPanel
                  ciType="rack_element"
                  ciId={server.id}
                  companyId={companyId}
                  luns={companyLuns}
                  onChanged={refreshVolumes}
                />
              </div>
              <div className="sf2-card" style={{ marginTop: 16 }}>
                <h3>LUNs publicados</h3>
                <p className="sf2-hint" style={{ marginTop: 0 }}>
                  LUNs que <strong>este equipo publica</strong> a otros hosts (iSCSI Target / LIO).
                  Si el equipo es el que <em>consume</em> almacenamiento, se ve en los volúmenes de arriba.
                </p>
                <StorageLunsPanel
                  elementId={server.id}
                  companyId={companyId}
                  ports={serverPorts}
                  onChanged={refreshVolumes}
                />
              </div>
            </div>
          )}
          {!isCards && activeTab === 'almacenamiento' && isExisting && (
            <div className="dv-tab-pane">
              <StorageVolumesPanel
                ciType="rack_element"
                ciId={server.id}
                companyId={companyId}
                luns={companyLuns}
                onChanged={refreshVolumes}
              />
            </div>
          )}
          {!isCards && activeTab === 'luns' && isExisting && (
            <div className="dv-tab-pane">
              <p className="dv-hint" style={{ marginBottom: 10 }}>
                LUNs que <strong>este equipo publica</strong> a otros hosts: es el caso de un
                servidor Windows con el rol iSCSI Target (o un Linux con LIO) que exporta discos
                virtuales .vhdx a los hosts Hyper-V. Si el equipo es el que <em>consume</em>{' '}
                almacenamiento, lo verás en la pestaña «Almacenamiento».
              </p>
              <StorageLunsPanel
                elementId={server.id}
                companyId={companyId}
                ports={serverPorts}
                onChanged={refreshVolumes}
              />
            </div>
          )}
          {isCards && isExisting && activeTab === 'hardware' && (
            <div className="dv-tab-pane">
              <ServerCardsHardware
                key={`hardware-${componentsCount}`}
                serverId={server?.id}
                companyId={companyId}
                onOpenInventory={() => setShowComponentsPanel(true)}
                onCountChange={setNicsCount}
              />
            </div>
          )}
          {(!isCards || !isExisting) && activeTab === 'hardware' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Especificaciones</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Fabricante</label>
                    <div className="input-group">
                      <select
                        name="manufacturer"
                        value={form.manufacturer || ''}
                        onChange={e => { handleManufacturerChange(e.target.value); setAddingManufacturer(false); setNewManufacturer(''); }}
                        className="form-select"
                      >
                        <option value="">— Seleccionar —</option>
                        {manufacturerOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setAddingManufacturer(v => !v)}
                        title="Añadir al maestro"
                      >
                        Añadir
                      </button>
                    </div>
                    {addingManufacturer && (
                      <div className="input-group mt-2">
                        <input
                          className="form-control"
                          placeholder="Nuevo fabricante"
                          value={newManufacturer}
                          onChange={e => setNewManufacturer(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!newManufacturer.trim()) return;
                            const name = newManufacturer.trim();
                            setField('manufacturer', name);
                            await saveManufacturerToCatalog(name);
                            setNewManufacturer('');
                            setAddingManufacturer(false);
                          }}
                        >
                          Guardar
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }}>
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Modelo</label>
                    <div className="input-group">
                      <select
                        name="model"
                        value={form.model || ''}
                        onChange={e => { handleModelChange(e.target.value); setAddingModel(false); setNewModel(''); }}
                        className="form-select"
                        disabled={!form.manufacturer && filteredModelOptions.length === 0}
                      >
                        <option value="">— Seleccionar —</option>
                        {filteredModelOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setAddingModel(v => !v)}
                        title="Añadir al maestro"
                      >
                        Añadir
                      </button>
                    </div>
                    {addingModel && (
                      <div className="input-group mt-2">
                        <input
                          className="form-control"
                          placeholder="Nuevo modelo"
                          value={newModel}
                          onChange={e => setNewModel(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!newModel.trim()) return;
                            const name = newModel.trim();
                            setField('model', name);
                            await saveModelToCatalog(name);
                            setNewModel('');
                            setAddingModel(false);
                          }}
                        >
                          Guardar
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingModel(false); setNewModel(''); }}>
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Serial Number</label>
                    <input name="serial_number" value={form.serial_number || ''} onChange={handleChange} disabled={!editing} className="form-control" placeholder="SN123456789" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Asset Tag</label>
                    <input name="asset_tag" value={form.asset_tag || ''} onChange={handleChange} disabled={!editing} className="form-control" placeholder="AST-0001" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">CPU</label>
                    <input name="cpu" value={form.cpu} onChange={handleChange} disabled={!editing} className="form-control" placeholder="2 × 8 cores Intel Xeon" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">RAM (GB)</label>
                    <input type="number" name="ram_gb" value={form.ram_gb} onChange={handleChange} disabled={!editing} className="form-control" placeholder="64" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Almacenamiento (TB)</label>
                    <input type="number" step="0.1" name="storage_tb" value={form.storage_tb} onChange={handleChange} disabled={!editing} className="form-control" placeholder="2" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Hypervisor</label>
                    <input name="hypervisor" value={form.hypervisor} onChange={handleChange} disabled={!editing} className="form-control" placeholder="VMware / KVM / Baremetal" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cluster</label>
                    <input name="cluster" value={form.cluster} onChange={handleChange} disabled={!editing} className="form-control" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Licencia ── */}
          {activeTab === 'licencia' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Licencia del sistema operativo</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Licencia</label>
                    <input name="license" value={form.license} onChange={handleChange} disabled={!editing} className="form-control" placeholder="Ubuntu Advantage / RHEL Subscription" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="license_status" value={form.license_status} onChange={handleChange} disabled={!editing} className="form-select">
                      <option value="vigente">✅ Vigente</option>
                      <option value="expirada">❌ Expirada</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Vencimiento</label>
                    <input type="date" name="license_expires_at" value={form.license_expires_at || ''} onChange={handleChange} disabled={!editing} className="form-control" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Organización ── */}
          {activeTab === 'organizacion' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane sf2-org' : ''}`}>
              {isCards && (
                <div className="dv-section sf2-org-full sf2-org-summary">
                  <div className="dv-section-title">Resumen de organización</div>
                  <div className="sf2-hl">
                    <div className="sf2-kpi">
                      <span>Roles efectivos</span>
                      <b>{effectiveRoleEntries.length}</b>
                    </div>
                    <div className="sf2-kpi">
                      <span>Roles manuales</span>
                      <b>{(form.role_ids || []).length}</b>
                    </div>
                    <div className="sf2-kpi">
                      <span>Detectados por el agente</span>
                      <b>{detectedRoleLabels.length}</b>
                    </div>
                    <div className="sf2-kpi">
                      <span>Features de Windows</span>
                      <b>{Array.isArray(storageBlob?.windows_roles) ? storageBlob.windows_roles.length : 0}</b>
                    </div>
                    <div className="sf2-kpi">
                      <span>Software instalado</span>
                      <b>{Array.isArray(storageBlob?.installed_software) ? storageBlob.installed_software.length : 0}</b>
                    </div>
                  </div>
                </div>
              )}
              <div className="dv-section">
                <div className="dv-section-title">Responsabilidad y etiquetas</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Equipo responsable</label>
                    <input name="owner" value={form.owner} onChange={handleChange} disabled={!editing} className="form-control" placeholder="Equipo Apps" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Tags <span className="text-muted" style={{ fontSize: '0.75rem' }}>(separados por coma)</span></label>
                    <input name="tags" value={form.tags} onChange={handleChange} disabled={!editing} className="form-control" placeholder="critical, db, windows" />
                  </div>
                </div>
              </div>
              <div className="dv-section">
                <div className="dv-section-title">Roles / Servicios</div>
                <div className="form-text" style={{ marginBottom: 8 }}>
                  Los roles manuales y los detectados por el agente conviven: el agente refresca automáticamente los detectados; los manuales puedes añadirlos o quitarlos aquí.
                </div>
                <div style={{ display: 'grid', gap: 14 }}>
                  <div>
                    <div className="form-text" style={{ marginBottom: 6 }}>Roles manuales</div>
                    <select
                      className="form-select"
                      multiple
                      size={Math.min(8, (roleOptions?.length || 0) + 1)}
                      value={(form.role_ids || []).map(String)}
                      onChange={handleRolesChange}
                      disabled={!editing || (!isExisting && roleOptions.length === 0)}
                    >
                      {roleOptions.map(r => (
                        <option key={r.id} value={String(r.id)}>{r.code || r.name}</option>
                      ))}
                    </select>
                    <div className="form-text" style={{ marginTop: 4 }}>
                      Ctrl+click para selección múltiple. Se guardan al pulsar Guardar.
                    </div>
                    {selectedRoles.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {selectedRoles.map(role => (
                          <span
                            key={role.id || role.code || role.name}
                            className="sf2-role-badge sf2-role-badge--manual"
                          >
                            <span>{role.code || role.name}</span>
                            {role.id && (
                              <button
                                type="button"
                                onClick={() => {
                                  const ids = (form.role_ids || [])
                                    .map(Number)
                                    .filter(rid => rid && Number(rid) !== Number(role.id));
                                  setField('role_ids', ids);
                                }}
                                title="Quitar selección (se aplicará al guardar)"
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="form-text" style={{ marginBottom: 6 }}>Roles detectados por el agente</div>
                    {detectedRoleLabels.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {detectedRoleLabels.map(label => (
                          <span key={label} className="sf2-role-badge sf2-role-badge--agent">
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="form-text">Sin roles detectados por el agente.</div>
                    )}
                  </div>

                  <div>
                    <div className="form-text" style={{ marginBottom: 6 }}>Vista combinada</div>
                    {effectiveRoleEntries.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {effectiveRoleEntries.map((entry) => (
                          <span
                            key={entry.key}
                            className={`sf2-role-badge ${entry.source === 'manual' ? 'sf2-role-badge--manual' : 'sf2-role-badge--agent'}`}
                          >
                            {entry.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="form-text">Este servidor no tiene roles visibles todavia.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Active Directory / Domain Controller ── */}
              <div className="dv-section sf2-org-full sf2-org-ad">
                <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Active Directory
                  {form.ad_dc && (
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 700,
                      background: 'rgba(59,130,246,.18)', color: '#93c5fd',
                      borderRadius: 4, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '.04em',
                    }}>Domain Controller</span>
                  )}
                </div>
                <p className="form-text" style={{ marginBottom: 10 }}>
                  Marca este servidor como Domain Controller para que aparezca en el esquema
                  de Active Directory (vista de topología en el servicio AD asociado).
                </p>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="sf2-switch">
                      <input
                        type="checkbox"
                        checked={!!form.ad_dc}
                        onChange={e => setField('ad_dc', e.target.checked)} disabled={!editing}
                      />
                      <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                      <span className="sf2-switch-label">Este servidor es un Domain Controller</span>
                    </label>
                  </div>

                  {form.ad_dc && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Dominio (FQDN)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="ej. corp.empresa.local"
                          value={form.ad_domain || ''}
                          onChange={e => setField('ad_domain', e.target.value)} disabled={!editing}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Sitio AD</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="ej. Default-First-Site-Name / Madrid / Barcelona"
                          value={form.ad_site || ''}
                          onChange={e => setField('ad_site', e.target.value)} disabled={!editing}
                        />
                        <div className="form-text">
                          Si lo dejas vacío se usará la sede física (location) del servidor.
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="sf2-switch">
                          <input
                            type="checkbox"
                            checked={!!form.ad_global_catalog}
                            onChange={e => setField('ad_global_catalog', e.target.checked)} disabled={!editing}
                          />
                          <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                          <span className="sf2-switch-label">Es Global Catalog (GC)</span>
                        </label>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Roles FSMO</label>
                        <div className="sf2-switches">
                          {[
                            ['schema_master', 'Schema Master'],
                            ['domain_naming_master', 'Domain Naming Master'],
                            ['pdc_emulator', 'PDC Emulator'],
                            ['rid_master', 'RID Master'],
                            ['infrastructure_master', 'Infrastructure Master'],
                          ].map(([value, label]) => {
                            const current = Array.isArray(form.ad_fsmo_roles) ? form.ad_fsmo_roles : [];
                            const checked = current.includes(value);
                            return (
                              <label key={value} className="sf2-switch">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!editing}
                                  onChange={e => {
                                    const next = e.target.checked
                                      ? [...new Set([...current, value])]
                                      : current.filter(v => v !== value);
                                    setField('ad_fsmo_roles', next);
                                  }}
                                />
                                <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                                <span className="sf2-switch-label">{label}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="form-text">
                          Solo uno de cada rol FSMO debería estar marcado en todo el bosque/dominio. La vista de topología avisará si detecta conflictos.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ── Windows Server roles (agent-detected) ── */}
              {Array.isArray(storageBlob?.windows_roles) && storageBlob.windows_roles.length > 0 && (
                <div className="dv-section sf2-org-full">
                  <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Roles de Windows Server detectados
                    <span className="sf2-count">{storageBlob.windows_roles.length}</span>
                  </div>
                  {(() => {
                    const roleItems = storageBlob.windows_roles.filter(r => r.feature_type === 'Role');
                    const featureItems = storageBlob.windows_roles.filter(r => r.feature_type !== 'Role');

                    return (
                      <>
                        {roleItems.length > 0 && (
                          <div className="sf2-subgroup">
                            <div className="sf2-subtitle">
                              <span aria-hidden="true">🧩</span>
                              Roles principales
                              <span className="sf2-count">{roleItems.length}</span>
                            </div>
                            <div className="sf2-chipset">
                              {roleItems.map((r, i) => (
                                <span key={`role-${i}`} title={r.name} className="sf2-pill sf2-pill--ac">
                                  {r.display_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {roleItems.length > 0 && featureItems.length > 0 && (
                          <div
                            style={{
                              marginTop: 12,
                              marginBottom: 8,
                              borderTop: '1px dashed rgba(148,163,184,.45)',
                              boxShadow: '0 1px 0 rgba(15,23,42,.35) inset',
                            }}
                          />
                        )}

                        {featureItems.length > 0 && (
                          <div className="sf2-subgroup">
                            <div className="sf2-subtitle">
                              <span aria-hidden="true">🛠️</span>
                              Features y servicios
                              <span className="sf2-count">{featureItems.length}</span>
                            </div>
                            <div className="sf2-chipset">
                              {featureItems.map((r, i) => (
                                <span key={`feature-${i}`} title={r.name} className="sf2-pill sf2-pill--ok">
                                  {r.display_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ── Software instalado: inventario editable + licencia ── */}
              <div className="dv-section sf2-org-full">
                <SoftwareInventoryPanel
                  companyId={companyId}
                  hostCiType="rack_element"
                  hostCiId={server?.id}
                  fallbackEntries={Array.isArray(storageBlob?.installed_software) ? storageBlob.installed_software : []}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Red / IP ── */}
          {isCards && activeTab === 'red' && (
            <div className="dv-tab-pane">
              {server?.id && (
                <div style={{ marginBottom: 16 }}>
                  <RemoteActions companyId={companyId} ciType="rack_element" ciId={server.id} />
                </div>
              )}
              {/* Diagrama desde el punto de vista del servidor: una cadena por NIC. */}
              {server?.id && (
                <div className="sf2-card" style={{ marginBottom: 16 }}>
                  <ConnectionDiagram
                    kind="server"
                    id={server.id}
                    companyId={companyId}
                    title={`Conexiones de ${form.name || server?.name || 'el servidor'}`}
                  />
                </div>
              )}
              <ServerCardsNetwork
                key={`red-${nicsCount}`}
                serverId={server?.id}
                companyId={companyId}
                teams={Array.isArray(storageBlob?.network_teams) ? storageBlob.network_teams : []}
                onOpenInventory={() => setShowComponentsPanel(true)}
                onCountChange={setNicsCount}
              />
              <div className="sf2-grid" style={{ marginTop: 16 }}>
                <div className="sf2-card">
                  <h3>IPs asignadas<span className="sf2-count">{ipCount}</span></h3>
                  <ServerCardsIps
                    key={`ips-${ipRefresh}`}
                    objectType="server"
                    objectId={server?.id}
                    companyId={companyId}
                    onCountChange={setIpCount}
                  />
                  <details
                    className="sf2-ip-manage"
                    onToggle={(e) => {
                      const abierto = e.currentTarget.open;
                      setShowIpManager(abierto);
                      if (!abierto) setIpRefresh((n) => n + 1);
                    }}
                  >
                    <summary>Gestionar IPs (añadir, editar, primaria…)</summary>
                    {showIpManager && (
                      <ServerCardsIpManager
                        objectId={server?.id}
                        companyId={companyId}
                        teams={Array.isArray(storageBlob?.network_teams) ? storageBlob.network_teams : []}
                        onCountChange={setIpCount}
                        onChanged={() => setIpRefresh((n) => n + 1)}
                      />
                    )}
                  </details>
                </div>
                <div className="sf2-card">
                  <NetworkConnectionsPanel
                    objectType="rack_element"
                    objectId={server?.id}
                    companyId={companyId}
                    elementName={form.name || server?.name}
                    teams={Array.isArray(storageBlob?.network_teams) ? storageBlob.network_teams : []}
                  />
                </div>
              </div>
            </div>
          )}
          {!isCards && activeTab === 'red' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <ElementIpPanel
                  objectType="server"
                  objectId={server?.id}
                  companyId={companyId}
                  elementName={form.name || server?.name}
                  onCountChange={setIpCount}
                  onBmcCreated={refreshComponentsCount}
                />
                <NetworkConnectionsPanel
                  objectType="rack_element"
                  objectId={server?.id}
                  companyId={companyId}
                  elementName={form.name || server?.name}
                  teams={Array.isArray(storageBlob?.network_teams) ? storageBlob.network_teams : []}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Accesos ── */}
          {activeTab === 'access' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="rack_element"
                  objectId={server?.id}
                  companyId={companyId}
                  elementName={form.name || server?.name}
                  defaultHost={bmcIp || form.ip_address}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Alimentación ── */}
          {activeTab === 'power' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <h3 className="dv-section-title">Conexiones PDU / Alimentación eléctrica</h3>
                <PowerConnectionsPanel
                  consumerType="rack_element"
                  consumerId={server?.id}
                  companyId={companyId}
                  rackId={form.rack_id || server?.rack_id}
                  elementName={form.name || server?.name}
                />
              </div>
            </div>
          )}

          {activeTab === 'historial' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <h3 className="dv-section-title">Historial de cambios</h3>
                <ServerChangelog serverId={server?.id} companyId={companyId} />
              </div>
            </div>
          )}

          {activeTab === 'monitoring' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <h3 className="dv-section-title">Monitorización en tiempo real</h3>
                <ServerMonitoringPanel serverId={server?.id} companyId={companyId} />
              </div>
            </div>
          )}

          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null, { force: true })}
          />
          </div>
        </form>

        {/* ── Right sidebar ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="server"
          ciId={server?.id}
          tipoLegible="el servidor"
        />
      </div>


    </div>

    {/* ── Sync Modal ── */}
    {showSyncModal && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={closeSyncModal} />
        <div style={{
          position: 'relative', width: 480, maxWidth: '92vw', background: 'var(--slate-100)',
          border: '1px solid var(--slate-200)', borderRadius: 12, boxShadow: '0 25px 50px rgba(0,0,0,.25)',
          padding: '1.5rem', color: 'var(--text-primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h5 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
              📡 {syncMode === 'hypervisor_sync' ? 'Sincronizacion de hipervisor y maquinas virtuales' : 'Sincronizacion de agente'}
            </h5>
            <button type="button" onClick={closeSyncModal}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>

          {/* Server info */}
          <div style={{ background: 'var(--slate-200)', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: 16, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{form.name || form.hostname}</span>
            {server?.agent_id && <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>· {server.agent_id.slice(0, 12)}…</span>}
            <span style={{ marginLeft: 8 }}>· canal: <strong style={{ color: 'var(--text-primary)' }}>{agentTransportInfo.label}</strong></span>
          </div>

          {agentTransportInfo.mode !== 'websocket' && (
            <div style={{
              background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
              borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#d97706', marginBottom: 12,
            }}>
              ⚠ WebSocket no activo para este agente. Se está usando <strong>polling fallback</strong>.
              {agentTransportInfo.detail ? ` Motivo: ${agentTransportInfo.detail}` : ''}
            </div>
          )}

          {/* Steps timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {[
              { key: 'enqueueing', label: 'Enviando comando al servidor…', icon: '📤' },
              { key: 'pending', label: `Comando en cola — esperando que el agente lo recoja (${syncElapsed}s, poll ~60 s pero depende de la duración del ciclo)`, icon: '⏳' },
              {
                key: 'running',
                label: syncMode === 'hypervisor_sync'
                  ? `Agente sincronizando hipervisor y maquinas virtuales… (${syncElapsed}s)`
                  : `Agente ejecutando rescan del sistema… (${syncElapsed}s)`,
                icon: '🔄',
              },
              { key: 'done', label: 'Sincronización completada', icon: '✅' },
              { key: 'error', label: syncError || 'Error durante la sincronización', icon: '❌' },
            ].map((step) => {
              const stages = ['enqueueing', 'pending', 'running', 'done', 'error'];
              const currentIdx = stages.indexOf(syncStep);
              const stepIdx = stages.indexOf(step.key);
              const isError = syncStep === 'error';
              const isActive = step.key === syncStep;
              const isDone = !isError && stepIdx < currentIdx;
              if (step.key === 'error' && !isError) return null;
              if (isError && stepIdx > 0 && stepIdx < stages.indexOf('error') && stepIdx > currentIdx) return null;

              return (
                <div key={step.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0.7rem', borderRadius: 8,
                  background: isActive ? 'rgba(99,102,241,.12)' : 'transparent',
                  opacity: isDone ? 0.5 : isActive ? 1 : 0.3,
                  transition: 'all 0.3s',
                }}>
                  <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
                    {isDone ? '✓' : step.icon}
                  </span>
                  <span style={{
                    fontSize: '0.85rem', fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}>{step.label}</span>
                  {isActive && step.key !== 'done' && step.key !== 'error' && (
                    <span className="dv-spinner" style={{ width: 14, height: 14, marginLeft: 'auto' }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Result details */}
          {syncStep === 'done' && syncResult && (
            <div style={{
              background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.25)',
              borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#16a34a', marginBottom: 12,
            }}>
              {syncResult.message || (syncMode === 'hypervisor_sync'
                ? 'El agente ha sincronizado hipervisor y maquinas virtuales. La vista se actualizara automaticamente.'
                : 'El agente ha completado el escaneo. Los datos se actualizaran automaticamente.')}
            </div>
          )}

          {syncStep === 'error' && (
            <div style={{
              background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)',
              borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#dc2626', marginBottom: 12,
            }}>
              {syncError}
            </div>
          )}

          {!!syncNotice && syncStep !== 'error' && (
            <div style={{
              background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
              borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#d97706', marginBottom: 12,
            }}>
              {syncNotice}
            </div>
          )}

          {/* Agent offline warning */}
          {agentStatus === 'offline' && (syncStep === 'pending' || syncStep === 'running' || syncStep === 'enqueueing') && (
            <div style={{
              background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
              borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#d97706', marginBottom: 12,
            }}>
              ⚠ El agente está <strong>offline</strong>. El comando quedará pendiente hasta que vuelva a conectar.
            </div>
          )}

          {/* Close button */}
          <div style={{ textAlign: 'right' }}>
            <button type="button" onClick={closeSyncModal} style={{
              background: syncStep === 'done' ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : 'var(--slate-200)',
              color: syncStep === 'done' ? '#fff' : 'var(--text-primary)', border: 'none', borderRadius: 6,
              padding: '0.4rem 1.2rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}>
              {syncStep === 'done' || syncStep === 'error' ? 'Cerrar' : 'Cancelar'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Confirmar borrado (diálogo común) ── */}
    {showDeleteConfirm && (
      <Dialog
        title="Eliminar servidor"
        busy={saving}
        onClose={() => setShowDeleteConfirm(false)}
        footer={(
          <>
            <button className="list-btn-secondary" type="button" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
              Cancelar
            </button>
            <button className="list-btn-danger" type="button" onClick={handleDelete} disabled={saving}>
              {saving ? 'Eliminando…' : 'Eliminar'}
            </button>
          </>
        )}
      >
        <p>
          ¿Seguro que quieres eliminar el servidor <strong>{form.name}</strong>? Se eliminarán también
          sus componentes, discos y conexiones de red y alimentación. Esta acción no se puede deshacer.
        </p>
      </Dialog>
    )}

    </>
  );
}

export default ServerForm;
